// Import React modules
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  ChangeEvent,
} from "react";
import axios from "axios";
// Import other modules
import {
  doc,
  getDocs,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  where,
  serverTimestamp,
} from "firebase/firestore";
// Import Firebase config
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../firebase";
import { htmlToText } from "html-to-text";
import { EditorProvider, FloatingMenu, BubbleMenu } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import LinearProgress from "@mui/material/LinearProgress";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
// import { MenuItem, Select } from "@material-ui/core";
import { styled } from "@mui/material/styles";
import {
  Tabs,
  Tab,
  Box,
  Button,
  TextField,
  Autocomplete,
  Typography,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AttachmentIcon from "@mui/icons-material/Attachment";
// Import components
import HtmlTooltip from "../components/ToolTips";
import TipTap from "../components/TipTap";
import NoteContainer from "../components/NoteContainer";
// Import utils
import AudioRecorder from "../utils/AudioRecorder";
// Import assets
import loadingSpinner from "../assets/icons/loading_spinner.svg";
import xMarkIcon from "../assets/icons/xmark.svg";
// Import CSS stylesheets
import "../styles/variables.css";
import "../styles/layoutStyles.css";
import "../styles/buttonStyles.css";
import "../styles/textStyles.css";
import UAParser from "ua-parser-js";
import RegenerateAudioRecorder from "../utils/RegenerateAudioRecorder";
import { MeddbasePID, MeddbasePV1 } from "../types/meddbase";
import { format } from "date-fns";
import Tooltip from "@mui/material/Tooltip";
import jsPDF from "jspdf";
import { pushToMeddbase } from "../utils/meddbaseUtils";

interface Patient {
  id: string;
  title: string;
  first_name: string;
  last_name: string;
  dob: Date;
  sex: string;
  address_1: string;
  address_2: string;
  address_3: string;
  post_code: string;
  email: string;
  phone: string;
  phone_type: string;
  clinical_info: string;
}

interface Recording {
  id: string;
  templateID: string;
  templateCategory: string;
  templateDisplayName: string;
  templateIsPreset: boolean;
  timestamp: Timestamp;
  progress: string;
  summary: string;
  htmlSummary: string;
  noteType: string;
  transcription: string;
  updatedTranscript: string;
  sessionNotes: string;
  patientId?: string;
  patientName?: string;
  clippeOutput?: string;
}

interface Summary {
  id: string;
  timestamp: Timestamp;
  summary: string;
  noteType: string;
  instruction: string;
  process_time: number;
  summaryChange: string;
}

interface User {
  id: string;
  account?: string;
  stripeSessionId?: string;
  clinikoApiKey?: string;
  meddbaseUserId?: string;
  cancelled?: string;
  currentPeriodEnd?: string;
  title?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  initials?: string;
  photoUrl?: string;
  dob?: Date;
  sex?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  postcode?: string;
  email?: string;
  phone?: string;
  phoneType?: string;
  occupation?: string;
  clinicName?: string;
  clinicRole?: string;
  clinicAddress1?: string;
  clinicAddress2?: string;
  clinicAddress3?: string;
  clinicPostCode?: string;
  clinicEmail?: string;
  clinicPhone?: string;
  clinicPhoneType?: string;
}

interface TranscribeTabContentProps {
  uid: string;
  clinikoApiKey: string;
  user: any;
  recording: boolean;
  currentUser: User;
  setRecording: React.Dispatch<React.SetStateAction<boolean>>;
  unactionedHl7Messages: Array<{
    pid: MeddbasePID;
    pv1: MeddbasePV1;
    id: string;
    timestamp: Date;
  }>;
  setUnactionedHl7Messages: React.Dispatch<React.SetStateAction<any>>;
  meddbasePID: MeddbasePID | null;
  setMeddbasePID: React.Dispatch<React.SetStateAction<MeddbasePID | null>>;
  meddbasePV1: MeddbasePV1 | null;
}

interface StyledTabsProps {
  children?: React.ReactNode;
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

interface StyledTabProps {
  label: string;
}

interface Template {
  id: string;
  displayName: string;
  description: string;
  visiblePrompt: string;
  hiddenPrompt: string;
  createdAt?: Date | null;
}

interface Templates {
  clinicalNotes: Template[];
  letters: Template[];
  meetingNotes: Template[];
}

const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs
    {...props}
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
  />
))({
  "& .MuiTabs-indicator": {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  "& .MuiTabs-indicatorSpan": {
    width: "90%",
    backgroundColor: "var(--color-brand-green)",
  },
});

const StyledTab = styled((props: StyledTabProps) => (
  <Tab disableRipple {...props} />
))(({ theme }) => ({
  color: "rgba(0, 0, 0, 0.85)",
  "&.Mui-selected": {
    color: "var(--color-brand-green)",
  },
  "&.Mui-focusVisible": {
    backgroundColor: "rgba(100, 95, 228, 0.32)",
  },
}));

const TranscribeTabContent: React.FC<TranscribeTabContentProps> = ({
  uid,
  clinikoApiKey,
  user,
  recording,
  currentUser,
  setRecording,
  unactionedHl7Messages,
  setUnactionedHl7Messages,
  meddbasePID,
  setMeddbasePID,
  meddbasePV1,
}) => {
  // State declarations
  const [openCustomTemplateModal, setOpenCustomTemplateModal] =
    useState<boolean>(false);
  const [selectedNoteType, setSelectedNoteType] =
    useState<string>("Adaptive AI");
  const [customTemplate, setCustomTemplate] = useState<string>("");
  // const [recording, setRecording] = useState<boolean>(false);
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [fullFilename, setFullFilename] = useState<string>("");
  const [summaryProgressText, setSummaryProgressText] =
    useState<string>("Begin processing");
  const [summaryAvailable, setSummaryAvailable] = useState<boolean>(false);
  const [summaryProgressPercentage, setSummaryProgressPercentage] =
    useState<number>(0);
  const [summary, setSummary] = useState<string>("");
  const [transcription, setTranscription] = useState<string>("");
  const [transcriptionAvailable, setTranscriptionAvailable] =
    useState<boolean>(false);
  const [sessionNotes, setSessionNotes] = useState<string>("");
  const [sessionNotesUpload, setSessionNotesUpload] = useState<string>("");
  const [copied, setCopied] = useState<boolean>(false);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [currentRecording, setCurrentRecording] = useState<Recording[]>([]);
  const previousRecording = useRef<Recording[]>([]);
  const previousSummaryVersions = useRef<Summary[]>([]);
  const [openRegenerateDialog, setOpenRegenerateDialog] =
    useState<boolean>(false);
  const [regenerateNoteType, setRegenerateNoteType] = useState<string>("");
  const [regenerateInstruction, setRegenerateInstruction] =
    useState<string>("");
  const [showRegenerateSuccessfulAlert, setShowRegenerateSuccessfulAlert] =
    useState<boolean>(false);
  const [loadingRegenerateSummary, setLoadingRegenerateSummary] =
    useState<boolean>(false);
  const [isDownloadingDocument, setIsDownloadingDocument] =
    useState<boolean>(false);
  const [showAssignToPatientModal, setShowAssignToPatientModal] = useState<{
    isVisible: boolean;
    recordingId: string;
    noteType: string;
    summary: string;
    htmlSummary: string;
    isAssigned: boolean;
    patientName: string;
    assignedPatient: Patient | null | undefined; // Update the type here
  }>({
    isVisible: false,
    recordingId: "",
    noteType: "",
    summary: "",
    htmlSummary: "",
    isAssigned: false,
    patientName: "",
    assignedPatient: null,
  });
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [summaryVersions, setSummaryVersions] = useState<Summary[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalVersions, setTotalVersions] = useState(0);
  const [timestamp, setTimestamp] = useState<Timestamp | null>(null);
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [presetTemplates, setPresetTemplates] = useState<Templates>({
    clinicalNotes: [],
    letters: [],
    meetingNotes: [],
  });
  const [userTemplates, setUserTemplates] = useState<Templates>({
    clinicalNotes: [],
    letters: [],
    meetingNotes: [],
  });
  const [combinedTemplates, setCombinedTemplates] = useState<Templates>({
    clinicalNotes: [],
    letters: [],
    meetingNotes: [],
  });
  const [selectedCategory, setSelectedCategory] = useState<
    keyof Templates | ""
  >("clinicalNotes");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [isPreset, setIsPreset] = useState<boolean>(true);
  const [regenerateSelectedCategory, setRegenerateSelectedCategory] = useState<
    keyof Templates | ""
  >("clinicalNotes");
  const [regenerateSelectedTemplate, setRegenerateSelectedTemplate] =
    useState<string>("");
  const [regenerateIsPreset, setRegenerateIsPreset] = useState<boolean>(true);
  const [regenerateSelectedDisplayName, setRegenerateSelectedDisplayName] =
    useState<string>("");
  const [pushToCliniko, setPushToCliniko] = useState<boolean>(false);
  // Progress mapping for summary generation
  const progressMapping: { [key: string]: number } = {
    "": 0,
    "Preparing recording": 10,
    "Processing recording": 20,
    "Transcribing recording": 50,
    "Generating summary": 70,
    "Summary generated": 100,
  };
  const [isPatientInfoCorrect, setIsPatientInfoCorrect] = useState(false);
  const [selectedAssignPatient, setSelectedAssignPatient] =
    useState<Patient | null>(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [filteredSearchPatients, setFilteredSearchPatients] = useState<
    Patient[]
  >([]);
  const [assignNoteLocation, setAssignNoteLocation] =
    useState("treatmentNotes");
  const [isAssigningToPatient, setIsAssigningToPatient] = useState(false);
  const [cloudFunctionPatients, setCloudFunctionPatients] = useState<Patient[]>(
    []
  );
  const [isCloudFunctionPatient, setIsCloudFunctionPatient] =
    useState<boolean>(false);
  const [templateID, setTemplateID] = useState<string>("");
  const [templateCategory, setTemplateCategory] = useState<string>("");
  const [searchFirstName, setSearchFirstName] = useState("");
  const [searchLastName, setSearchLastName] = useState("");
  const [loadingSearchPatientResults, setLoadingSearchPatientResults] =
    useState(false);
  const [searchPatientResults, setSearchPatientResults] = useState<Patient[]>(
    []
  );
  const [selectedPatientIndex, setSelectedPatientIndex] = useState<
    string | undefined
  >(undefined);
  // Using searchDone to show "No patient results found."
  const [searchDone, setSearchDone] = useState(false);
  const [clippeOutput, setClippeOutput] = useState("");
  const [loadingAnalyseWithClippe, setLoadingAnalyseWithClippe] =
    useState(false);
  const [mode, setMode] = useState<"generate" | "dictate">("generate");
  const [uploadingAudioFile, setUploadingAudioFile] = useState(false);
  const [errorUploadMessage, setErrorUploadMessage] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [newTranscript, setNewTranscript] = useState<string>("");
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [toRegenerateRecId, setToRegenerateRecId] = useState("");
  const [toRegenerateRecSummary, setToRegenerateRecSummary] = useState("");
  const [toRegenerateRecTimeStamp, setToRegenerateRecTimeStamp] =
    useState<Date | null>(null);
  const [toRegenerateIndex, setToRegenerateIndex] = useState(0);
  const [selectedMessageId, setSelectedMessageId] = useState("");
  const [showPushToMeddbaseModal, setShowPushToMeddbaseModal] = useState({
    isVisible: false,
    recordingId: "",
    noteType: "",
    summary: "",
  });
  const [isMeddbasePatientInfoCorrect, setIsMeddbasePatientInfoCorrect] =
    useState(false);
  const [isRemoveFromQueueAfterPush, setIsRemoveFromQueueAfterPush] =
    useState(false);

  // useEffect(() => {
  //   if (meddbasePID) {
  //     console.log("Meddbase PID received:", meddbasePID);
  //     // You can now access the properties with type safety, e.g.:
  //     console.log("Timestamp:", meddbasePID.timestamp);
  //     console.log(
  //       "Patient Name:",
  //       meddbasePID.patientName.givenName,
  //       meddbasePID.patientName.familyName
  //     );
  //     console.log("Date of Birth:", meddbasePID.dateTimeOfBirth);
  //     console.log(
  //       "Address:",
  //       meddbasePID.patientAddress.streetAddress,
  //       meddbasePID.patientAddress.city
  //     );
  //     // Use this data to update the UI or perform other actions
  //   }
  // }, [meddbasePID]);

  useEffect(() => {
    if (selectedMessageId) {
      const selectedMessage = unactionedHl7Messages.find(
        (msg) => msg.id === selectedMessageId
      );
      if (selectedMessage) {
        setMeddbasePID({
          ...selectedMessage.pid,
          id: selectedMessage.id,
          timestamp: selectedMessage.timestamp,
        });
      }
    }
  }, [selectedMessageId, unactionedHl7Messages, setMeddbasePID]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (uid) {
        const userDocRef = doc(db, "users", uid);
        const userDocSnapshot = await getDoc(userDocRef);
        const userData = userDocSnapshot.data();
        if (userData) {
          if (userData.defaultTemplateCategory) {
            setSelectedCategory(userData.defaultTemplateCategory);
            await updateDoc(userDocRef, {
              selectedTemplateCategory: userData.defaultTemplateCategory,
            });
          }
          if (userData.defaultTemplateId) {
            setSelectedTemplate(userData.defaultTemplateId);
            await updateDoc(userDocRef, {
              selectedTemplateID: userData.defaultTemplateId,
              selectedTemplateIsPreset: userData.defaultTemplateIsPreset,
            });
          }
        }
      }
    };

    const fetchTemplates = async () => {
      const templateCategories = ["clinicalNotes", "letters", "meetingNotes"];
      let fetchedPresetTemplates: Templates = {
        clinicalNotes: [],
        letters: [],
        meetingNotes: [],
      };
      let fetchedUserTemplates: Templates = {
        clinicalNotes: [],
        letters: [],
        meetingNotes: [],
      };

      const fetchCategoryTemplates = async (
        docRef: any,
        category: keyof Templates,
        templates: Templates
      ) => {
        const categoryCollectionRef = collection(docRef, category);
        const categoryQuerySnapshot = await getDocs(categoryCollectionRef);

        const fetchedTemplates: Template[] = [];

        categoryQuerySnapshot.forEach((docSnapshot) => {
          if (docSnapshot.exists()) {
            const typeData = docSnapshot.data();
            const template: Template = {
              id: docSnapshot.id,
              displayName: typeData.displayName || "",
              description: typeData.description || "",
              visiblePrompt: typeData.visiblePrompt || "",
              hiddenPrompt: typeData.hiddenPrompt || "",
              createdAt: typeData.createdAt
                ? new Date(typeData.createdAt)
                : null,
            };

            fetchedTemplates.push(template);
          }
        });

        // Sort the fetched templates by createdAt if it exists
        fetchedTemplates.sort((a, b) => {
          const aCreatedAt = a.createdAt ? a.createdAt.getTime() : 0;
          const bCreatedAt = b.createdAt ? b.createdAt.getTime() : 0;
          return aCreatedAt - bCreatedAt;
        });

        templates[category] = fetchedTemplates;
      };

      // Fetching preset and user templates
      if (uid) {
        for (const category of templateCategories) {
          await Promise.all([
            fetchCategoryTemplates(
              doc(db, "moticsTemplates", "current"),
              category as keyof Templates,
              fetchedPresetTemplates
            ),
            fetchCategoryTemplates(
              doc(db, "users", uid, "templates", "current"),
              category as keyof Templates,
              fetchedUserTemplates
            ),
          ]);
        }
      }

      // Merging fetchedPresetTemplates and fetchedUserTemplates
      const combinedTemplates: Templates = templateCategories.reduce(
        (acc, category) => {
          acc[category as keyof Templates] = [
            ...(fetchedPresetTemplates[category as keyof Templates] || []),
            ...(fetchedUserTemplates[category as keyof Templates] || []),
          ];
          return acc;
        },
        {} as Templates
      );

      setPresetTemplates(fetchedPresetTemplates);
      setUserTemplates(fetchedUserTemplates);
      setCombinedTemplates(combinedTemplates);
    };
    setPageLoading(true);
    fetchUserData();
    fetchTemplates();
    setPageLoading(false);
  }, [uid]);

  // This function will be called when the user tries to close the tab or window
  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (recording) {
      event.preventDefault();
      event.returnValue =
        "Are you sure you want to leave? Your recording will be lost.";
    }
  };

  const formatDateOfBirth = (dateString: string): string => {
    if (!dateString || dateString.length !== 8) return "Invalid Date";

    const year = dateString.slice(0, 4);
    const month = dateString.slice(4, 6);
    const day = dateString.slice(6, 8);

    const date = new Date(`${year}-${month}-${day}`);
    return format(date, "dd/MM/yyyy");
  };

  function camelCaseToWords(str: string): string {
    // Split the string into words based on capital letters.
    const words = str.match(/[A-Z]?[a-z]+|[A-Z]+(?![a-z])/g) || [];

    // Capitalize the first word and join the rest of the words with spaces.
    const result = words
      .map((word, index) =>
        index === 0
          ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          : word
      )
      .join(" ");

    return result;
  }

  // Function to get progress percentage based on key
  const getProgress = (key: string): number => {
    return progressMapping[key] || 0;
  };

  const processSummary = (
    noteType: string | undefined | null,
    rawSummary: string | undefined | null
  ): string => {
    if (!rawSummary) {
      return "";
    }

    let htmlContent = "";

    if (!rawSummary) {
      return htmlContent;
    } else if (!/<h1>.+?<\/h1>/.test(rawSummary)) {
      if (noteType) {
        htmlContent += `<h1>${noteType}</h1><br>`;
      }
    }
    let rawSummaryCopy = rawSummary;
    const lines = rawSummaryCopy.split(/(?<=\n)/);
    lines.forEach((line) => {
      // const trimmedLine = line.trim();
      const trimmedLine = line.trim();
      const fullLine = line;
      let temp = trimmedLine;

      if (trimmedLine.startsWith("##") && trimmedLine.endsWith("##")) {
        temp = `<h2>${trimmedLine.slice(2, -2)}</h2>`;
      } else if (trimmedLine.startsWith("**") && trimmedLine.endsWith("**")) {
        temp = `<strong>${trimmedLine.slice(2, -2)}</strong>`;
      } else if (trimmedLine.endsWith(":")) {
        temp = `<strong>${trimmedLine}</strong>`;
      } else {
        temp = fullLine
          .replace(/(\d+\.\s*)?\*\*(.+?)\*\*/g, "<strong>$1$2</strong>")
          .replace(
            /^[ \t]{1,2}- (.+?)(?=\n|$)/gm,
            "<p>&nbsp;&nbsp;&nbsp;&nbsp;- $1</p>"
          )
          .replace(/^- (.+?)(?=\n|$)/gm, "<p>- $1</p>")
          .replace(/^\s*-(.+?):/gm, "<strong>- $1:</strong>");
      }

      if (!temp.match(/<\/?[a-z][\s\S]*>/i)) {
        temp = `<p>${temp}</p>`;
      }

      // else if (temp === "\n") {
      //   temp = "<br />";
      // }

      htmlContent += temp;
    });

    // htmlContent = htmlContent.replace(/<\/p>\s*<br\s*\/?>\s*<p>/g, "</p><p>");

    return htmlContent || rawSummary;
  };

  useEffect(() => {
    if (recording) {
      // Add the event listener when recording starts
      window.addEventListener("beforeunload", handleBeforeUnload);
    } else {
      // Remove the event listener when recording ends
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }

    // Clean up the event listener when the component is unmounted
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [recording]);

  // useEffect to fetch user data and listen to recording updates
  useEffect(() => {
    const fetchData = async () => {
      if (user && user.uid) {
        const userDocRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(userDocRef);

        if (docSnap.exists()) {
          const userData = docSnap.data();
          setSelectedNoteType(userData.noteType || "SOAP Note");

          const recordingsCollectionRef = collection(
            db,
            "users",
            user.uid,
            "recordings"
          );
          const q = query(
            recordingsCollectionRef,
            orderBy("timestamp", "desc"),
            limit(1)
          );

          const unsubscribeSnapshot = onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach(async (change) => {
              if (change.type === "added" || change.type === "modified") {
                // console.log("Change type: ", change.type);
                const data = change.doc.data();
                const id = change.doc.id;

                // Process the main summary
                const processedSummary = await processSummary(
                  data.displayName || data.noteType || "",
                  data.summary
                );

                // Other properties
                const timestamp = data.timestamp;
                const progress = data.progress;
                const transcription = data.transcription;
                const updatedTranscript = data.updatedTranscript;
                const sessionNotes = data.sessionNotes;
                const patientId = data.patientId;
                const patientName = data.patientName;
                const templateID = data.templateID || "";
                const templateCategory = data.templateCategory || "";
                const templateIsPreset = data.templateIsPreset || false;
                const noteType = data.displayName || data.noteType;
                const clippeOutput = data.clippeOutput;

                const newRecording = {
                  id: id,
                  timestamp: timestamp,
                  progress: progress,
                  templateID: templateID,
                  templateCategory: templateCategory,
                  templateIsPreset: templateIsPreset,
                  noteType: noteType,
                  summary: processedSummary,
                  transcription: transcription,
                  updatedTranscript: updatedTranscript,
                  sessionNotes: sessionNotes,
                  patientId: patientId,
                  patientName: patientName,
                  clippeOutput: clippeOutput,
                } as Recording;

                // Set state
                setSummaryProgressText(data.progress || "Begin processing");
                setSummaryProgressPercentage(getProgress(data.progress));
                setSummary(processedSummary || "");
                setSummaryAvailable(Boolean(data.summary));
                setTranscription(
                  data.updatedTranscript?.trim() || data.transcription || ""
                );
                setSessionNotes(data.sessionNotes || "No notes saved.");
                setTranscriptionAvailable(Boolean(data.transcription));
                setRegenerateNoteType(data.noteType || selectedNoteType);
                setCurrentRecording([newRecording]);
                setTimestamp(timestamp);
                setClippeOutput(data.clippeOutput);

                // Fetch and process summaryVersions
                const summaryVersionsRef = collection(
                  db,
                  `users/${user.uid}/recordings/${id}/summaryVersions`
                );

                const summaryVersionsSnapshot = await getDocs(
                  summaryVersionsRef
                );

                if (!summaryVersionsSnapshot.empty) {
                  // Process each summaryVersion asynchronously and wait for all to complete
                  const processedSummaries = await Promise.all(
                    summaryVersionsSnapshot.docs.map(async (doc) => {
                      const summaryData = doc.data();
                      // console.log("Summary data:", summaryData.summary);
                      const processedVersionSummary = await processSummary(
                        summaryData.displayName || summaryData.noteType || "",
                        summaryData.summary
                      );
                      return {
                        id: doc.id,
                        timestamp: summaryData.timestamp,
                        summary: processedVersionSummary,
                        // summary: summaryData.summary,
                        noteType:
                          summaryData.displayName || summaryData.noteType,
                        instruction: summaryData.instruction,
                        process_time: summaryData.process_time,
                        summaryChange: summaryData.summaryChange,
                      };
                    })
                  );

                  // Sort and set the processed summaries
                  processedSummaries.sort((a, b) => a.timestamp - b.timestamp);
                  setSummaryVersions(processedSummaries);
                  setTotalVersions(processedSummaries.length);
                  setCurrentIndex(
                    processedSummaries.length ===
                      previousSummaryVersions.current.length
                      ? currentIndex
                      : processedSummaries.length - 1
                  );
                } else {
                  setSummaryVersions([]);
                  setTotalVersions(0);
                  setCurrentIndex(-1); // No summaries available
                }
              }
            });
          });

          return unsubscribeSnapshot;
        }
      }
    };

    fetchData();
  }, [user]);

  useEffect(() => {
    previousRecording.current = currentRecording;
  }, [currentRecording]);

  useEffect(() => {
    previousSummaryVersions.current = summaryVersions;
  }, [summaryVersions]);

  useEffect(() => {
    setCurrentIndex(totalVersions - 1);
  }, [totalVersions]);

  const getPatientName = (patientId: string) => {
    const patient = patients.find((p) => p.id === patientId);
    return patient ? `${patient.first_name} ${patient.last_name}` : "Unknown";
  };

  // Only show options if 3 or more characters have been entered
  useEffect(() => {
    if (searchInputValue.length >= 3) {
      setFilteredSearchPatients(
        patients.filter((patient) =>
          `${patient.first_name} ${patient.last_name}`
            .toLowerCase()
            .includes(searchInputValue.toLowerCase())
        )
      );
    } else {
      setFilteredSearchPatients([]);
    }
  }, [searchInputValue, patients]);

  useEffect(() => {
    setIsCloudFunctionPatient(
      !!cloudFunctionPatients.find(
        (patient) => patient.id === selectedPatientId
      )
    );
  }, [cloudFunctionPatients, selectedPatientId]);

  // const fetchPatients = async () => {
  //   try {
  //     // Fetch patients from the cloud function
  //     const cloudFunctionResponse = await axios.post(
  //       "https://europe-west2-motics-consultations.cloudfunctions.net/cliniko-fetch-patients-staging",
  //       { uid }
  //     );
  //     const cloudFunctionPatients: Patient[] = (
  //       cloudFunctionResponse.data.patients || []
  //     ).map((patient: any) => ({
  //       ...patient,
  //       dob: new Date(patient.date_of_birth),
  //       phone:
  //         patient.patient_phone_numbers.length > 0
  //           ? patient.patient_phone_numbers[0].normalized_number
  //           : "",
  //       phone_type:
  //         patient.patient_phone_numbers.length > 0
  //           ? patient.patient_phone_numbers[0].phone_type
  //           : "",
  //     }));

  //     setCloudFunctionPatients(cloudFunctionPatients);

  //     // Fetch patients from the patient collection in Firebase
  //     const userRef = doc(db, "users", uid);
  //     const userDoc = await getDoc(userRef);
  //     if (userDoc.exists()) {
  //       const patientsCollectionRef = collection(userRef, "patients");
  //       const patientsSnapshot = await getDocs(patientsCollectionRef);
  //       const firebasePatients: Patient[] = patientsSnapshot.docs.map(
  //         (doc) => ({
  //           id: doc.id,
  //           ...doc.data(),
  //         })
  //       ) as Patient[];

  //       // Merge the patients from both sources
  //       const mergedPatients = [...cloudFunctionPatients, ...firebasePatients];
  //       setPatients(mergedPatients);
  //       // console.log("Patients array:", patients);
  //     } else {
  //       console.error("User document does not exist");
  //     }
  //   } catch (error) {
  //     console.error("Error fetching patients from cloud function:", error);
  //     // Fetch patients from the patient collection in Firebase only
  //     const userRef = doc(db, "users", uid);
  //     const patientsCollectionRef = collection(userRef, "patients");
  //     const patientsSnapshot = await getDocs(patientsCollectionRef);
  //     const firebasePatients: Patient[] = patientsSnapshot.docs.map((doc) => ({
  //       id: doc.id,
  //       ...doc.data(),
  //     })) as Patient[];
  //     setPatients(firebasePatients);
  //     // console.log("Patients array (Firebase only):", firebasePatients);
  //   }
  // };

  const handleProgressUpdate = useCallback(() => {
    if (summaryProgressPercentage < 100) {
      // Random increment between 0.5% and 1.5%
      const increment = Math.random();
      setSummaryProgressPercentage((prevProgress) =>
        Math.min(Math.round(prevProgress + increment), 100)
      );
    }
  }, [summaryProgressPercentage]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (
      summaryProgressText === "Generating summary" &&
      summaryProgressPercentage < 70
    ) {
      setSummaryProgressPercentage(70);
    }

    if (
      summaryProgressText === "Generating summary" &&
      summaryProgressPercentage >= 70 &&
      summaryProgressPercentage < 100
    ) {
      timer = setInterval(handleProgressUpdate, 2000);
    }

    if (summaryProgressText === "Summary generated") {
      setSummaryProgressPercentage(100);
      if (timer) clearInterval(timer); // Clear the interval if it's set
    }

    return () => {
      if (timer) clearInterval(timer); // Cleanup interval on unmount
    };
  }, [summaryProgressText, summaryProgressPercentage, handleProgressUpdate]);

  useEffect(() => {
    // console.log("Updated Patients array:", patients);
  }, [patients]);

  // useEffect(() => {
  //   if (uid) {
  //     fetchPatients();
  //   }
  // }, [uid]);

  // Function to update the noteType in the user's Firestore document
  const updateNoteTypeInFirestore = async (
    templateID: string,
    templateCategory: string,
    templateIsPreset: boolean
  ): Promise<void> => {
    const userDocRef = doc(db, "users", uid);
    // console.log("User doc ref in updateNoteTypeInFirestore:", type);
    await updateDoc(userDocRef, {
      // templateID: templateID,
      // templateCategory: templateCategory,
      // templateIsPreset: templateIsPreset,
      selectedTemplateID: templateID,
      selectedTemplateCategory: templateCategory,
      selectedTemplateIsPreset: templateIsPreset,
    });
  };

  const handleRecordingClick = () => {
    if (selectedTemplate !== "") {
      setRecording((prevState) => !prevState);
    }
  };

  const handleAudioUrl = (
    audioUrl: string,
    fullFilename: string,
    isSetCurrentRecording: boolean
  ) => {
    setAudioUrl(audioUrl);
    setFullFilename(`audioFiles/${fullFilename}`);
    setSummaryAvailable(false);
    if (isSetCurrentRecording) {
      setCurrentRecording([]);
    }
    // console.log("Audio URL in handleAudioUrl:", audioUrl);
    // console.log("Filename in handleAudioUrl:", fullFilename);
  };

  const allowedAudioTypes = [
    // MP3
    "audio/mpeg",
    "audio/mp3",
    "audio/mpga",
    "audio/x-mp3",
    "audio/x-mpeg",
    // MP4
    "audio/mp4",
    "audio/x-mp4",
    "audio/mp4a-latm",
    "audio/mpeg4-generic",
    "audio/x-mpeg4-generic",
    "video/mp4",
    "video/x-mp4",
    // MP2
    // "audio/mpeg",
    // "audio/x-mpeg",
    // AAC
    "audio/aac",
    "audio/x-aac",
    // WAV
    "audio/wav",
    "audio/x-wav",
    "audio/wave",
    "audio/x-pn-wav",
    // FLAC
    "audio/flac",
    "audio/x-flac",
    // PCM (usually wrapped in WAV or AIFF container)
    "audio/l16",
    "audio/x-pcm",
    // M4A
    "audio/m4a",
    "audio/x-m4a",
    // Ogg
    "audio/ogg",
    "audio/x-ogg",
    "video/ogg",
    "application/ogg",
    // Opus (usually in Ogg container)
    "audio/opus",
    "audio/x-opus",
    // WebM
    "audio/webm",
    "video/webm",
    // catch all
    "audio/*",
    "video/*",
  ];

  // const isAllowedFileExtension = (filename: string) => {
  //   const allowedExtensions = [
  //     "mp3",
  //     "mp4",
  //     "mp2",
  //     "aac",
  //     "wav",
  //     "flac",
  //     "pcm",
  //     "m4a",
  //     "ogg",
  //     "opus",
  //     "webm",
  //   ];
  //   const extension = filename.split(".").pop()?.toLowerCase();
  //   return extension ? allowedExtensions.includes(extension) : false;
  // };

  const isAllowedFileExtension = (filename: string) => {
    const extension = filename.split(".").pop()?.toLowerCase();
    // This regex matches common audio and video file extensions
    const audioVideoExtensionRegex = /^(mp3|mp4|m4a|aac|wav|ogg|webm)$/i;
    return extension ? audioVideoExtensionRegex.test(extension) : false;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const file = event.target.files[0];
      if (file) {
        if (
          allowedAudioTypes.includes(file.type) ||
          isAllowedFileExtension(file.name)
        ) {
          setUploadFile(file);
          setErrorUploadMessage("");
        } else {
          setErrorUploadMessage("Please upload a supported audio file.");
          setUploadFile(null);
        }
      }
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) return;
    setUploadingAudioFile(true);
    try {
      // Write initial data to Firestore to get the recordingId
      const userDocRef = doc(db, "users", user.uid);
      const recordingsCollectionRef = collection(userDocRef, "recordings");
      const recordingData = {
        timestamp: serverTimestamp(),
        progress: "Preparing recording",
        sessionNotes: sessionNotesUpload,
        templateID: templateID,
        templateCategory: templateCategory,
        // templateIsPreset: templateIsPreset,
        operatingSystem: getOperatingSystem(new UAParser().getResult()),
        device: getDevice(new UAParser().getResult()),
        browser: getBrowser(new UAParser().getResult()),
        ipAddress: await getIpAddress(),
        mode: mode,
      };

      const docRef = await addDoc(recordingsCollectionRef, recordingData);
      const recordingId = docRef.id;

      // Create filename with recordingId
      const filename = `${user.uid}_${new Date().toISOString()}_${recordingId}`;
      const fileExtension = uploadFile.name.split(".").pop();
      const fullFilename = `audioFiles/${filename}.${fileExtension}`;

      // Upload file to storage
      let storageRef;
      if (mode === "dictate") {
        storageRef = ref(storage, `dictateAudioFiles/${fullFilename}`);
      } else {
        storageRef = ref(storage, `audioFiles/${fullFilename}`);
      }
      const fileRef = ref(storage, fullFilename);
      await uploadBytes(fileRef, uploadFile);
      console.log("File uploaded to storage");
      const audioUrl = await getDownloadURL(fileRef);

      // Update the recording document with the audio URL and filename
      await updateDoc(doc(recordingsCollectionRef, recordingId), {
        audioUrl: audioUrl,
        fullFilename: fullFilename,
        progress: "Processing recording",
      });

      handleAudioUrl(audioUrl, fullFilename, true);
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setUploadingAudioFile(false);
    }
  };

  function getOperatingSystem(result: UAParser.IResult): string {
    return `${result.os.name} ${result.os.version}`;
  }

  function getDevice(result: UAParser.IResult): string {
    // Device type might not be defined for all user agents, e.g., desktop browsers
    const deviceType = result.device.type ? result.device.type : "Desktop";
    const deviceModel = result.device.model ? result.device.model : "N/A";
    return `${deviceType} - ${deviceModel}`;
  }

  function getBrowser(result: UAParser.IResult): string {
    return `${result.browser.name} ${result.browser.version}`;
  }

  async function getIpAddress(): Promise<string> {
    try {
      const response = await axios.get("https://api.ipify.org?format=json");
      return response.data.ip;
    } catch (error) {
      console.error("Error fetching IP address:", error);
      return "N/A";
    }
  }

  const handleOpenCustomTemplateModal = async () => {
    if (uid) {
      const userDocRef = doc(db, "users", uid);
      try {
        getDoc(userDocRef).then((docSnap) => {
          if (docSnap.exists()) {
            const userData = docSnap.data();
            if (userData.template) {
              setCustomTemplate(userData.template);
            } else {
              setCustomTemplate("");
            }
          }
        });
      } catch (error) {
        console.error("Error fetching user's template:", error);
      }
      setOpenCustomTemplateModal(true);
    } else {
      // console.log("User not found in handleOpenTemplateModal");
    }
  };

  const handleCloseCustomTemplateModal = (): void => {
    setOpenCustomTemplateModal(false);
  };

  const handleSaveCustomTemplate = async (): Promise<void> => {
    const userDocRef = doc(db, "users", uid);
    try {
      await updateDoc(userDocRef, { template: customTemplate });
      alert("Custom template saved successfully!");
    } catch (error) {
      console.error("Error updating document:", error);
    }
    handleCloseCustomTemplateModal();
  };

  // const handleNoteTypeChange = (type: string): void => {
  //   setSelectedNoteType(type);
  //   updateNoteTypeInFirestore(type); // Update Firestore
  //   if (type === "Custom") {
  //     handleOpenCustomTemplateModal();
  //   }
  // };

  const handleCopyToClipboard = () => {
    let content = "";
    if (tabValue === 0) {
      // If the Summary tab is active
      content =
        summaryVersions.length > 0
          ? processSummary(
              summaryVersions[currentIndex].noteType,
              summaryVersions[currentIndex].summary
            )
          : processSummary(selectedNoteType, summary);
    } else if (tabValue === 1) {
      // If the Transcript tab is active
      content = transcription;
    } else if (tabValue === 2) {
      // If the Session Notes tab is active
      content = sessionNotes;
    } else if (tabValue === 3) {
      // If the Clippe Output tab is active
      content = clippeOutput;
    }

    // // Replace <br> and <p> tags with new lines, and remove other HTML tags
    // const plainText = content
    //   .replace(/<br\s*\/?>/gi, "\n\n")
    //   .replace(/<p>/gi, "\n\n")
    //   .replace(/<[^>]+>/g, "");

    // // Remove HTML tags and copy to clipboard
    // const element = document.createElement("textarea");
    // element.value = plainText;
    // // element.value = content.replace(/<[^>]+>/g, "");
    // document.body.appendChild(element);
    // element.select();
    // navigator.clipboard.writeText(element.value);
    // document.body.removeChild(element);

    // Use Clipboard API to write HTML to clipboard
    const stripHtmlTags = (html: string): string => {
      const div = document.createElement("div");
      div.innerHTML = html;
      return div.textContent || div.innerText || "";
    };

    navigator.clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([`<style>p { margin: 0; }</style>${content}`], {
          type: "text/html",
        }),
        "text/plain": new Blob([stripHtmlTags(content)], {
          type: "text/plain",
        }),
      }),
    ]);

    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1000);
  };

  const updateContentOnFirebase = async (
    recordingId: string,
    summaryVersionId: string,
    newContent: string,
    type: string
  ) => {
    try {
      const recordingDocRef = doc(db, `users/${uid}/recordings`, recordingId);
      if (type === "summary") {
        await updateDoc(recordingDocRef, { summary: newContent });
        currentRecording[0].summary = newContent;
      } else if (type === "transcription") {
        await updateDoc(recordingDocRef, { transcription: newContent });
        currentRecording[0].transcription = newContent;
      } else if (type === "notes" && newContent !== "No notes saved.") {
        await updateDoc(recordingDocRef, { sessionNotes: newContent });
        currentRecording[0].sessionNotes = newContent;
      }
      if (summaryVersionId !== "") {
        const regeneratedSummaryColRef = collection(
          db,
          `users/${uid}/recordings/${recordingId}/summaryVersions`
        );
        const regeneratedSummaryDocRef = doc(
          regeneratedSummaryColRef,
          summaryVersionId
        );
        await updateDoc(regeneratedSummaryDocRef, { summary: newContent });
        // console.log("newContent:", newContent);
        const updatedSummaryVersions = [...summaryVersions];
        updatedSummaryVersions[currentIndex] = {
          ...updatedSummaryVersions[currentIndex],
          summary: newContent,
        };
        setSummaryVersions(updatedSummaryVersions);

        // summaryVersions.map((summary) =>
        //   summary.id === summaryVersionId
        //     ? { ...summary, summary: newContent }
        //     : summary
        // );
      }
    } catch (error) {
      console.error("Error updating content:", error);
      // Handle any errors here
    }
  };

  const handleOpenRegenerateDialog = (
    recId: string,
    recSummary: string,
    recTimeStamp: any,
    index: number
  ) => {
    setToRegenerateRecId(recId);
    setToRegenerateRecSummary(recSummary);
    setToRegenerateRecTimeStamp(recTimeStamp);
    setToRegenerateIndex(index);
    setOpenRegenerateDialog(true);
  };

  const handleCloseRegenerateDialog = () => {
    setNewTranscript("");
    setOpenRegenerateDialog(false);
  };

  const handleRegenerateSummary = async (
    recId: string,
    recSummary: string,
    recTimeStamp: any
  ) => {
    if (!regenerateSelectedTemplate) {
      alert("Please select a template before regenerating the summary.");
      return;
    }

    const userId = auth.currentUser ? auth.currentUser.uid : null;
    if (!userId) {
      console.error("No authenticated user found.");
      return;
    }

    setLoadingRegenerateSummary(true);
    handleCloseRegenerateDialog();

    try {
      const regeneratedSummaryColRef = collection(
        db,
        `users/${userId}/recordings/${currentRecording[0].id}/summaryVersions`
      );

      if (!currentRecording || currentRecording.length === 0) {
        console.error("No current recording available");
        // return; // or handle this case appropriately
      }

      // Check if the original summary exists in the summaryVersions collection
      const regeneratedSummariesSnapshot = await getDocs(
        regeneratedSummaryColRef
      );
      if (regeneratedSummariesSnapshot.empty) {
        await addDoc(regeneratedSummaryColRef, {
          templateID: currentRecording[0].templateID,
          templateCategory: currentRecording[0].templateCategory,
          templateIsPreset: currentRecording[0].templateIsPreset,
          templateDisplayName: currentRecording[0].noteType,
          noteType: currentRecording[0].noteType,
          summaryChange: "Original",
          instruction: "",
          timestamp: currentRecording[0].timestamp,
          summary: currentRecording[0].summary,
        });
      }

      // Add the new regenerated summary placeholder
      const newSummaryRef = await addDoc(regeneratedSummaryColRef, {
        templateID: regenerateSelectedTemplate,
        templateCategory: regenerateSelectedCategory,
        templateIsPreset: regenerateIsPreset,
        templateDisplayName: regenerateSelectedDisplayName,
        noteType: regenerateSelectedDisplayName,
        instruction: regenerateInstruction,
        summaryChange: "Regenerated",
        timestamp: new Date(),
        summary: "",
        newTranscript: newTranscript || "",
      });

      const regenerateSummaryDocId = newSummaryRef.id;

      // Retrieve information about the patient and practitioner
      const practitionerTitle = currentUser.title
        ? currentUser.title
        : "[practitioner title]";
      const practitionerName = `${
        currentUser.firstName
          ? currentUser.firstName
          : "[practitioner's first name]"
      } ${
        currentUser.lastName
          ? currentUser.lastName
          : "[practitioner's last name]"
      }`;
      const practitionerClinic = currentUser.clinicName
        ? currentUser.clinicName
        : "[practitioner's clinic]";
      const practitionerClinicAddress1 = currentUser.clinicAddress1
        ? currentUser.clinicAddress1
        : "[practitioner's clinic address line 1]";
      const practitionerClinicAddress2 = currentUser.clinicAddress2
        ? currentUser.clinicAddress2
        : "[practitioner's clinic address line 2]";
      const practitionerClinicAddress3 = currentUser.clinicAddress3
        ? currentUser.clinicAddress3
        : "[practitioner's clinic address line 3]";
      const practitionerClinicPostCode = currentUser.clinicPostCode
        ? currentUser.clinicPostCode
        : "[practitioner's clinic postcode]";
      const practitionerClinicContactNumber = currentUser.clinicPhone
        ? currentUser.clinicPhone
        : "[practitioner's clinic contact number]";
      let patientName = null;
      let patientId = null;
      let patientDOB = null;
      let patientAddress1 = null;
      let patientAddress2 = null;
      let patientAddress3 = null;
      let patientPostcode = null;
      let patientEmail = null;
      let patientSex = null;

      const recordingDocRef = doc(
        db,
        `users/${userId}/recordings/${currentRecording[0].id}`
      );
      const recordingDoc = await getDoc(recordingDocRef);
      let updatedTranscript = "";
      if (recordingDoc.exists()) {
        const recordingData = recordingDoc.data();
        const existingTranscript =
          updatedTranscript && updatedTranscript.trim() !== ""
            ? updatedTranscript
            : recordingData.transcription || "";

        if (newTranscript && newTranscript.trim() !== "") {
          const today = new Date();
          const formattedDate =
            today
              .toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })
              .replace(",", "") + (today.getHours() < 12 ? " AM" : " PM");

          updatedTranscript = `${existingTranscript}\n\n<strong>Transcript Addendum - ${formattedDate}</strong>\n${newTranscript}`;
        } else {
          updatedTranscript = existingTranscript;
        }
        patientName = recordingData.patientName
          ? recordingData.patientName
          : null;
        patientId = recordingData.patientId ? recordingData.patientId : null;
        patientDOB = recordingData.patientDOB ? recordingData.patientDOB : null;
        patientAddress1 = recordingData.patientAddress1
          ? recordingData.patientAddress1
          : null;
        patientAddress2 = recordingData.patientAddress2
          ? recordingData.patientAddress2
          : null;
        patientAddress3 = recordingData.patientAddress3
          ? recordingData.patientAddress3
          : null;
        patientPostcode = recordingData.patientPostcode
          ? recordingData.patientPostcode
          : null;
        patientEmail = recordingData.patientEmail
          ? recordingData.patientEmail
          : null;
        patientSex = recordingData.patientSex ? recordingData.patientSex : null;
        patientName = patientName ? patientName : "[patient's name]";
        patientDOB = patientDOB
          ? formatDate(patientDOB.toDate())
          : "[patient's date of birth]";
        patientAddress1 = patientAddress1
          ? patientAddress1
          : "[patient's address line 1]";
        patientAddress2 = patientAddress2
          ? patientAddress2
          : "[patient's address line 2]";
        patientAddress3 = patientAddress3
          ? patientAddress3
          : "[patient's address line 3]";
        patientPostcode = patientPostcode
          ? patientPostcode
          : "[patient's postcode]";
        patientEmail = patientEmail ? patientEmail : "[patient's email]";
        patientSex = patientSex ? patientSex : "[patient's sex]";

        await updateDoc(recordingDocRef, {
          transcription: updatedTranscript,
        });
      }

      const response = await axios.post(
        "https://europe-west2-motics-consultations.cloudfunctions.net/regenerate-summary",
        {
          userId: userId,
          recId: currentRecording[0].id,
          regenerateSummaryDocId: regenerateSummaryDocId,
          practitionerTitle,
          practitionerName,
          practitionerClinic,
          practitionerClinicAddress1,
          practitionerClinicAddress2,
          practitionerClinicAddress3,
          practitionerClinicPostCode,
          practitionerClinicContactNumber,
          patientName,
          patientDOB,
          patientAddress1,
          patientAddress2,
          patientAddress3,
          patientPostcode,
          patientEmail,
          patientSex,
        }
      );

      if (response) {
        console.log("response: ", response);
        const summaryDocRef = doc(
          db,
          `users/${userId}/recordings/${currentRecording[0].id}/summaryVersions`,
          regenerateSummaryDocId
        );
        const summaryDocSnapshot = await getDoc(summaryDocRef);

        if (summaryDocSnapshot.exists()) {
          const summaryData = summaryDocSnapshot.data();
          const summary = summaryData.summary;
          const process_time = summaryData.process_time;

          const processedRegeneratedSummary = processSummary(
            regenerateSelectedDisplayName || "",
            summary
          );

          const newSummaryVersion = {
            id: regenerateSummaryDocId,
            timestamp: Timestamp.now(),
            summary: processedRegeneratedSummary,
            htmlSummary: summary,
            noteType: regenerateSelectedDisplayName,
            instruction: regenerateInstruction,
            process_time: process_time,
            summaryChange: "Regenerated",
          };

          // Update UI with both original (if needed) and new summary
          setSummaryVersions((prevSummaryVersions) => {
            let updatedSummaryVersions = [...prevSummaryVersions];

            // If there are no summary versions, add the original summary first
            if (prevSummaryVersions.length === 0) {
              const originalSummaryVersion = {
                id: "original",
                timestamp: currentRecording[0].timestamp,
                summary: processSummary("", currentRecording[0].summary),
                htmlSummary: currentRecording[0].summary,
                noteType: currentRecording[0].noteType,
                instruction: "",
                process_time: 0,
                summaryChange: "Original",
              };
              updatedSummaryVersions.push(originalSummaryVersion);
            }

            updatedSummaryVersions.push(newSummaryVersion);

            const totalVersions = updatedSummaryVersions.length;

            setTotalVersions(totalVersions);

            return updatedSummaryVersions;
          });
        } else {
          console.error("Summary data is missing from the response");
        }
      }
      setShowRegenerateSuccessfulAlert(true);

      // setTimeout(() => {
      //   setShowRegenerateSuccessfulAlert(false);
      // }, 5000);
    } catch (error) {
      console.error("Error saving regenerated summary:", error);
      alert("Failed to regenerate the summary. Please try again.");
    } finally {
      setLoadingRegenerateSummary(false);
      setRegenerateInstruction("");
    }
  };

  const handleAnalyseWithClippe = async (recId: string, recSummary: string) => {
    try {
      const userId = auth.currentUser ? auth.currentUser.uid : null;
      if (!userId) {
        console.error("No authenticated user found.");
        return;
      }

      setLoadingAnalyseWithClippe(true);

      const response = await fetch(
        "https://europe-west2-motics-consultations.cloudfunctions.net/generate-clippe-output",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ note: recSummary }),
        }
      );
      // console.log("CLIPPE response: ", response);
      const data = await response.json();
      // console.log("CLIPPE output: ", data.output);
      const clippeOutput = data.output;

      if (clippeOutput) {
        const recordingRef = doc(db, `users/${userId}/recordings/${recId}`);
        await updateDoc(recordingRef, { clippeOutput: clippeOutput });
        setClippeOutput(clippeOutput);
      } else {
        console.error("Data is missing from the response");
      }
    } catch (error) {
      console.error("Error analysing with CLIPPE:", error);
      alert("Failed to analyse with CLIPPE. Please try again.");
    } finally {
      setLoadingAnalyseWithClippe(false);
    }
  };

  const handleAssignToPatient = async (patient: Patient, location: string) => {
    setIsAssigningToPatient(true);
    if (!selectedAssignPatient) {
      alert("Selected patient not found. Please try again.");
      return;
    }

    // Extract necessary data from the modal state
    const { recordingId, noteType, summary } = showAssignToPatientModal;
    // console.log("Summary", summary);
    const htmlSummary = processSummary(noteType, summary);
    // console.log("newHtmlSummary", newHtmlSummary);
    if (!recordingId) {
      console.error("Recording ID is undefined.");
      return;
    }

    try {
      const recordingDocRef = doc(db, `users/${uid}/recordings`, recordingId);
      const recordingDoc = await getDoc(recordingDocRef);

      let timestamp;
      if (recordingDoc.exists()) {
        const recordingData = recordingDoc.data();
        timestamp = recordingData.timestamp.toDate();
      } else {
        // console.log("Recording document does not exist");
        timestamp = new Date();
      }

      await updateDoc(recordingDocRef, {
        patientId: selectedAssignPatient.id,
        patientName: `${selectedAssignPatient.first_name} ${selectedAssignPatient.last_name}`,
        patientDOB: selectedAssignPatient.dob,
        patientAddress1: selectedAssignPatient.address_1
          ? selectedAssignPatient.address_1
          : "Not provided",
        patientAddress2: selectedAssignPatient.address_2
          ? selectedAssignPatient.address_2
          : "Not provided",
        patientAddress3: selectedAssignPatient.address_3
          ? selectedAssignPatient.address_3
          : "Not provided",
        patientPostcode: selectedAssignPatient.post_code || "Not provided",
        patientEmail: selectedAssignPatient.email || "Not provided",
        patientSex: selectedAssignPatient.sex || "Not provided",
      });

      const patientNotesCollectionRef = collection(
        db,
        `users/${uid}/patients/${selectedAssignPatient.id}/patientNotes`
      );
      const patientNoteData = {
        timestamp: timestamp,
        noteType: noteType,
        summary: summary,
        htmlSummary: htmlSummary,
        recordingId: recordingId,
      };

      const patientNoteQuery = query(
        patientNotesCollectionRef,
        where("recordingId", "==", recordingId)
      );
      const querySnapshot = await getDocs(patientNoteQuery);

      if (querySnapshot.empty) {
        await addDoc(patientNotesCollectionRef, patientNoteData);
      } else {
        // console.log(
        //   "Document with recordingId already exists in patient notes collection"
        // );
      }

      try {
        const clinikoResponse = await axios.post(
          "https://europe-west2-motics-consultations.cloudfunctions.net/cliniko-push-treatment-notes",
          {
            uid,
            clinikoApiKey,
            patient_id: selectedAssignPatient.id,
            title: `Motics Copilot Note: ${new Date().toLocaleString()}`,
            note_content: htmlSummary,
            // location: location,
          }
        );
        // console.log("Cliniko response:", clinikoResponse);
        // Update the patient's note document with the Cliniko note ID
        const clinikoNoteID = clinikoResponse.data.id;
        // console.log("Cliniko note ID:", clinikoNoteID);
        const recordingRef = doc(db, `users/${uid}/recordings`, recordingId);
        await updateDoc(recordingRef, { clinikoNoteID: clinikoNoteID });
        // const patientNoteRef = doc(
        //   db,
        //   `users/${uid}/patients/${selectedPatient.id}/patientNotes`,
        //   recordingId
        // );
        // await updateDoc(patientNoteRef, { clinikoNoteID });
      } catch (error) {
        console.error("Error pushing summary to Cliniko:", error);
      }

      // console.log("Summary assigned to patient successfully.");
      alert("Summary assigned to patient successfully.");
    } catch (error) {
      console.error("Error updating recording document:", error);
      alert("Failed to assign the summary to the patient. Please try again.");
    }

    setIsAssigningToPatient(false);
    setSelectedAssignPatient(null);
    setSearchPatientResults([]);
    setSearchDone(false);
    setIsPatientInfoCorrect(false);
    setShowAssignToPatientModal({
      isVisible: false,
      recordingId: "",
      noteType: "",
      summary: "",
      htmlSummary: "",
      isAssigned: false,
      patientName: "",
      assignedPatient: null,
    });
  };

  function renderNoteTypeSelector() {
    // Correct typing for the event parameter based on Material-UI's Select
    const handleTemplateChange = async (e: SelectChangeEvent) => {
      const templateId = e.target.value; // templateId is a string due to SelectChangeEvent
      // console.log(templateId);
      setSelectedTemplate(templateId);

      if (!selectedCategory) return;

      const template =
        combinedTemplates[selectedCategory] &&
        combinedTemplates[selectedCategory].find((t) => t.id === templateId);
      if (template) {
        // Simulate an async operation (e.g., checking if the template is a preset)
        const isPresetTemplate = await checkIfPreset(
          templateId,
          selectedCategory
        );

        setTemplateID(templateId);
        setTemplateCategory(selectedCategory);
        setIsPreset(isPresetTemplate);
        // TODO: Should probably just save the note type after recording because at the moment, updateNoteTypeInFirebase is not called at initialisation, this means the default note type will be the previous note type.
        updateNoteTypeInFirestore(
          templateId,
          selectedCategory,
          isPresetTemplate
        );
      }
    };

    // Placeholder async function for checking if a template is preset
    async function checkIfPreset(
      templateId: string,
      category: keyof Templates
    ): Promise<boolean> {
      // Simulate checking logic
      return new Promise((resolve) => {
        let isPreset = false;
        if (presetTemplates[category]) {
          isPreset = presetTemplates[category].some(
            (presetTemplate) => presetTemplate.id === templateId
          );
        }
        resolve(isPreset);
      });
    }

    return (
      <div>
        <div
          style={{
            fontSize: "1rem",
            fontWeight: "bold",
            textAlign: "center",
            marginTop: "2rem",
            marginBottom: "1rem",
          }}
        >
          Choose a template
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "2rem",
            marginBottom: "2rem",
            width: "100%",
          }}
        >
          <div style={{ width: "40%" }}>
            <div>Note Category</div>
            <FormControl fullWidth size="small">
              <Select
                labelId="demo-select-small-label"
                id="demo-select-small"
                value={selectedCategory}
                onChange={(e: SelectChangeEvent) => {
                  setSelectedCategory(e.target.value as keyof Templates);
                  setSelectedTemplate("");
                }}
                disabled={recording}
              >
                {Object.keys(combinedTemplates).map((category) => (
                  <MenuItem key={category} value={category}>
                    {camelCaseToWords(category)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div style={{ width: "40%" }}>
            <div>Note Template</div>
            <FormControl fullWidth size="small">
              <Select
                labelId="demo-select-small-label"
                id="demo-select-small"
                value={selectedTemplate || ""}
                onChange={handleTemplateChange}
                disabled={!selectedCategory || recording}
              >
                {selectedCategory &&
                  combinedTemplates[selectedCategory].map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.displayName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </div>
        </div>
      </div>
    );
  }

  // function renderNoteTypeSelector() {
  //   return (
  //     <div className="box-sizing-default transcribe-note-type-container">
  //       <div
  //         style={{
  //           display: "flex",
  //           justifyContent: "center",
  //           flexWrap: "wrap",
  //           gap: "10px",
  //           marginBottom: "1rem",
  //         }}
  //       >
  //         {[
  //           {
  //             type: "Adaptive AI",
  //             title:
  //               "Generates a SOAP note + any letters required based on your session.",
  //           },
  //           {
  //             type: "SOAP Note",
  //             title:
  //               "Document Subjective observations, Objective data, Assessment, and the Plan for care.",
  //           },
  //           {
  //             type: "SOAP Note (Short)",
  //             title:
  //               "Document Subjective observations, Objective data, Assessment, and the Plan for care in brief.",
  //           },
  //           {
  //             type: "Letter to Patient",
  //             title:
  //               "Generate a letter containing the patient's visit summary, treatment plan and care instructions.",
  //           },
  //           {
  //             type: "Letter to GP",
  //             title:
  //               "Generate a letter summarising patient's updates and care instructions for their GP.",
  //           },
  //           {
  //             type: "Referral to Speciality",
  //             title:
  //               "Generate a letter requesting for a specialist's evaluation of the patient.",
  //           },
  //           {
  //             type: "Custom",
  //             title: "Use a customised template to generate notes.",
  //           },
  //         ].map(({ type, title }) => (
  //           <HtmlTooltip
  //             arrow
  //             key={type}
  //             placement="top"
  //             title={
  //               <React.Fragment>
  //                 <Typography color="inherit">{type}</Typography>
  //                 {title}
  //               </React.Fragment>
  //             }
  //           >
  //             <button
  //               className={`button-classic ${
  //                 selectedNoteType === type ? "button-classic-green" : ""
  //               }`}
  //               onClick={() => handleNoteTypeChange(type)}
  //               disabled={recording}
  //               style={{ opacity: recording ? 0.5 : 1 }}
  //             >
  //               {type}
  //             </button>
  //           </HtmlTooltip>
  //         ))}
  //       </div>
  //     </div>
  //     {selectedNoteType === "Custom" &&
  //       openCustomTemplateModal &&
  //       renderCustomTemplateModal()}
  //   );
  // }

  const openAssignToPatientModal = () => {
    const isAssigned = currentRecording[0].patientId != null;
    const assignedPatient = isAssigned
      ? patients.find((patient) => patient.id === currentRecording[0].patientId)
      : null;

    const activeSummaryVersion =
      summaryVersions.length > 0 &&
      currentIndex >= 0 &&
      currentIndex < summaryVersions.length
        ? summaryVersions[currentIndex].summary
        : currentRecording[0].summary; // Fallback to the main summary if no versions are available

    const activeHtmlSummaryVersion =
      summaryVersions.length > 0 &&
      currentIndex >= 0 &&
      currentIndex < summaryVersions.length
        ? processSummary(
            summaryVersions[currentIndex].noteType,
            summaryVersions[currentIndex].summary
          )
        : currentRecording[0].htmlSummary;

    const activeNoteType =
      summaryVersions.length > 0 &&
      currentIndex >= 0 &&
      currentIndex < summaryVersions.length
        ? summaryVersions[currentIndex].noteType
        : currentRecording[0].noteType;

    setShowAssignToPatientModal({
      isVisible: true,
      recordingId: currentRecording[0].id,
      noteType: activeNoteType,
      summary: activeSummaryVersion,
      htmlSummary: activeHtmlSummaryVersion,
      isAssigned: isAssigned,
      patientName: currentRecording[0].patientName || "",
      assignedPatient: assignedPatient,
    });
  };

  const handleSearchAssignPatient = async (
    first_name: string,
    last_name: string
  ) => {
    setLoadingSearchPatientResults(true);
    const full_name = `${first_name ? first_name + " " : ""}${last_name}`;

    try {
      // console.log("Searching for: ", full_name);
      setSelectedAssignPatient(null);
      const cloudFunctionResponse = await axios.post(
        "https://europe-west2-motics-consultations.cloudfunctions.net/cliniko-fetch-patients",
        { uid, first_name: first_name.trim(), last_name: last_name.trim() }
      );

      console.log("Search patient results: ", cloudFunctionResponse.data);

      const cloudFunctionPatients: Patient[] = (
        cloudFunctionResponse.data || []
      ).map((patient: any) => ({
        ...patient,
        dob: new Date(patient.date_of_birth),
        phone:
          patient.patient_phone_numbers.length > 0
            ? patient.patient_phone_numbers[0].normalized_number
            : "",
        phone_type:
          patient.patient_phone_numbers.length > 0
            ? patient.patient_phone_numbers[0].phone_type
            : "",
      }));

      // const cloudFunctionPatients = Array.isArray(cloudFunctionResponse.data) ? cloudFunctionResponse.data.map((patient: any) => ({
      //   ...patient,
      //   dob: new Date(patient.date_of_birth),
      //   phone: patient.patient_phone_numbers.length > 0 ? patient.patient_phone_numbers[0].normalized_number : "",
      //   phone_type: patient.patient_phone_numbers.length > 0 ? patient.patient_phone_numbers[0].phone_type : "",
      // })) : [];

      setCloudFunctionPatients(cloudFunctionPatients);
      setSearchPatientResults(cloudFunctionPatients);
      setSearchDone(true);
      setSearchFirstName("");
      setSearchLastName("");
    } catch (error) {
      console.error("Error searching for patients:", error);
      // Handle error state or display error message
    } finally {
      setLoadingSearchPatientResults(false);
    }
  };

  useEffect(() => {
    // If there's only one search result, pre-select it in the dropdown
    if (searchPatientResults.length === 1) {
      setSelectedPatientIndex("0"); // Pre-select the first (and only) option
      setSelectedAssignPatient(searchPatientResults[0]);
    } else {
      setSelectedPatientIndex(""); // Reset or set to a default value as needed
    }
  }, [searchPatientResults]);

  const renderAssignToPatientModal = () => {
    const { isVisible, isAssigned, assignedPatient, patientName } =
      showAssignToPatientModal;
    if (!isVisible) return null;

    const handleSelectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedId = e.target.value;
      setSelectedPatientIndex(selectedId);
      const patient = searchPatientResults[parseInt(selectedId, 10)];
      setSelectedAssignPatient(patient || null);
    };

    return (
      <div className="box-sizing-default modal-background">
        <div className="box-sizing-default modals" id="assign-patient-modal">
          <button
            className="small-icon-button"
            onClick={() => {
              setShowAssignToPatientModal({
                isVisible: false,
                recordingId: "",
                noteType: "",
                summary: "",
                htmlSummary: "",
                isAssigned: false,
                patientName: "",
                assignedPatient: null,
              });
              setSelectedAssignPatient(null);
              setSearchPatientResults([]);
              setSearchDone(false);
              setSearchFirstName("");
              setSearchLastName("");
            }}
          >
            <img src={xMarkIcon} alt="close" />
          </button>
          <h2>Push to Cliniko</h2>
          {isAssigned && patientName && (
            <div className="alert-text">
              Please note that this summary has already been assigned to{" "}
              {patientName}.
            </div>
          )}
          <p>
            Search a patient by entering their first name or last name, or both.
          </p>
          <div
            className="box-sizing-default welcome-form"
            style={{ padding: "0" }}
          >
            <div
              style={{
                boxSizing: "border-box",
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <input
                className="box-sizing-default"
                style={{ width: "48%" }}
                type="text"
                name="firstName"
                placeholder="First name"
                value={searchFirstName}
                onChange={(e) => setSearchFirstName(e.target.value)}
              />
              <input
                className="box-sizing-default"
                style={{ width: "48%" }}
                type="text"
                name="lastName"
                placeholder="Last name"
                value={searchLastName}
                onChange={(e) => setSearchLastName(e.target.value)}
              />
            </div>
            <button
              className="button-copy"
              style={{
                display: "flex",
                alignItems: "center",
                alignSelf: "center",
                cursor: loadingRegenerateSummary ? "not-allowed" : "pointer",
              }}
              disabled={loadingSearchPatientResults}
              onClick={() => {
                if (!searchFirstName && !searchLastName) {
                  alert("Please enter at least one name to search.");
                } else {
                  handleSearchAssignPatient(searchFirstName, searchLastName);
                }
              }}
            >
              {loadingSearchPatientResults && (
                <img
                  src={loadingSpinner}
                  alt="Loading"
                  style={{
                    width: "1.5rem",
                    marginRight: "0.8rem",
                  }}
                />
              )}
              Search
            </button>
          </div>
          {searchPatientResults && searchPatientResults.length > 0 ? (
            <div style={{ marginTop: "1rem" }}>
              <p>Patient search results</p>
              <select
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "0.6rem",
                  border: "0.05rem solid #ccc",
                  borderRadius: "0.5rem",
                  overflowY: "auto",
                }}
                onChange={handleSelectionChange}
                value={selectedPatientIndex || ""} // Fallback to an empty string if undefined
              >
                <option value="" disabled>
                  Select a patient
                </option>
                {searchPatientResults.map((patient, index) => (
                  <option key={index} value={index}>
                    {`${patient.first_name} ${patient.last_name}`} -{" "}
                    {patient.dob
                      ? `(${formatDob(patient.dob.toString())})`
                      : ""}
                  </option>
                ))}
              </select>
            </div>
          ) : searchPatientResults &&
            searchPatientResults.length === 0 &&
            searchDone ? (
            <p>No patient results found.</p>
          ) : null}
          {/* <p>Assign the generated note to a patient.</p> */}

          {/* <Autocomplete
            value={selectedAssignPatient}
            onChange={(event, newValue: Patient | null) => {
              setSelectedAssignPatient(newValue);
            }}
            inputValue={searchInputValue}
            onInputChange={(event, newInputValue) => {
              setSearchInputValue(newInputValue);
            }}
            options={filteredSearchPatients}
            getOptionLabel={(patient) =>
              `${patient.first_name} ${patient.last_name}`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Enter at least 3 characters to search for a patient"
                variant="outlined"
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, patient) => (
              <Box
                component="li"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
                {...props}
              >
                <Typography
                  sx={{ textAlign: "left", flexGrow: 1 }}
                >{`${patient.first_name} ${patient.last_name}`}</Typography>
                <Typography sx={{ textAlign: "right", color: "grey" }}>
                {patient.dob instanceof Date ? `(${formatDob(patient.dob.toISOString())})` : ""}
                </Typography>
              </Box>
            )}
          /> */}
          {selectedAssignPatient && (
            <>
              <div style={{ marginTop: "2rem" }}>
                Please confirm the patient's information below before assigning.
              </div>
              <div
                style={{
                  padding: "1rem",
                  backgroundColor: "var(--color-brand-dark-green)",
                  color: "white",
                  border: "0.08rem solid silver",
                  borderRadius: "0.25rem",
                }}
              >
                <div>
                  <strong>Name: </strong>
                  {selectedAssignPatient.first_name}{" "}
                  {selectedAssignPatient.last_name}
                </div>
                <div>
                  <strong>Date of Birth: </strong>
                  {selectedAssignPatient.dob
                    ? formatDob(
                        selectedAssignPatient.dob instanceof Date
                          ? selectedAssignPatient.dob.toISOString()
                          : ""
                      )
                    : "Not provided"}
                </div>
                <div>
                  <strong>Sex: </strong>
                  {selectedAssignPatient.sex || "Not provided"}
                </div>
                <div>
                  <strong>Address: </strong>
                  {`${
                    selectedAssignPatient.address_1
                      ? selectedAssignPatient.address_1
                      : "Not provided"
                  }${
                    selectedAssignPatient.address_2
                      ? `, ${selectedAssignPatient.address_2}`
                      : ""
                  }${
                    selectedAssignPatient.address_3
                      ? `, ${selectedAssignPatient.address_3}`
                      : ""
                  }`}
                </div>
                <div>
                  <strong>Postcode: </strong>
                  {selectedAssignPatient.post_code || "Not provided"}
                </div>
                <div>
                  <strong>Email: </strong>
                  {selectedAssignPatient.email || "Not provided"}
                </div>
              </div>
              <div className="alert-text">
                If the patient's information is incorrect, please update the
                patient's information in Cliniko.
              </div>
              <div style={{ fontSize: "0.8rem" }}>
                <label>
                  <input
                    type="checkbox"
                    value="isPatientInfoCorrect"
                    onChange={(e) => setIsPatientInfoCorrect(e.target.checked)}
                  />
                  Confirm patient information is correct.
                </label>
              </div>
            </>
          )}
          {/* {selectedPatient && isPatientInfoCorrect && (
             <div style={{ marginTop: "1rem" }}>
             <p>Please choose where you would like to push this summary to.</p>
             <FormControl fullWidth>
               <InputLabel id="document-type-label">
                 Select location
               </InputLabel>
               <Select
                 // style={{marginTop:"1rem"}}
                 labelId="document-type-label"
                 id="documentType"
                 value={documentType}
                 label="Select location"
                 onChange={(event) => setDocumentType(event.target.value)}
               >
                 <MenuItem value="treatmentNotes">Treatment Notes</MenuItem>
                 <MenuItem value="files">Files</MenuItem>
          </Select>
          {isCloudFunctionPatient && (
            <div>
              <p>
                Would you like to push this summary to{" "}
                {getPatientName(selectedPatientId)}'s Cliniko record?
              </p>
              <div>
                <label>
                  Yes
                  <input
                    type="radio"
                    value="yes"
                    name="clinikoPush"
                    onChange={() => setPushToCliniko(true)}
                  />
                </label>
                <label>
                  No
                  <input
                    type="radio"
                    value="no"
                    name="clinikoPush"
                    onChange={() => setPushToCliniko(false)}
                  />
                </label>
              </div>
            </div>
          )} */}
          {selectedAssignPatient && isPatientInfoCorrect && (
            <button
              className="button-primary"
              disabled={isAssigningToPatient}
              style={{ marginTop: "2rem" }}
              onClick={() => {
                if (isPatientInfoCorrect && selectedAssignPatient !== null) {
                  handleAssignToPatient(
                    selectedAssignPatient,
                    "treatmentNotes"
                  );
                } else {
                  if (!isPatientInfoCorrect) {
                    alert(
                      "Please confirm that the patient information is correct before saving."
                    );
                  } else if (!selectedAssignPatient) {
                    alert("Please select a patient before saving.");
                  }
                }
              }}
            >
              Save
            </button>
          )}
        </div>
      </div>
    );
  };

  function renderCustomTemplateModal() {
    const customTemplateDescription = `Modify how the output of the transcription is generated with a custom template. For best results, we suggest you use headers and a brief description of what to include under each header. If no template is used, a default medical template will be applied.
    <br /><br />
    Ensure 'Custom' note type is selected to enable your template. If you are happy with the current template, just close this window.
    <br /><br />
    Suggested length: No more than 350 words.`;

    return (
      <div className="box-sizing-default modal-background">
        <div className="box-sizing-default modals" id="custom-template-modal">
          <button
            className="small-icon-button"
            onClick={handleCloseCustomTemplateModal}
          >
            <img src={xMarkIcon} className="small-icon-button" alt="close" />
          </button>
          <h3>Set custom template</h3>
          <p
            style={{ fontSize: "0.8rem" }}
            dangerouslySetInnerHTML={{ __html: customTemplateDescription }}
          ></p>
          <textarea
            className="box-sizing-default custom-template-modal-textarea"
            value={customTemplate}
            onChange={(event) => setCustomTemplate(event.target.value)}
          />
          <div style={{ width: "100%", textAlign: "right" }}>
            <button
              className="button-classic"
              onClick={handleSaveCustomTemplate}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderGenerateSummary() {
    return (
      <div className="box-sizing-default transcribe-center-container">
        <h3>Summary</h3>
        <div className="box-sizing-default transcribe-loading-summary-container">
          <LinearProgress
            variant="determinate"
            value={summaryProgressPercentage}
          />
          <p style={{ textAlign: "center" }}>{summaryProgressPercentage}%</p>
          {summaryProgressText !== "Error" && (
            <p style={{ textAlign: "center" }}>{summaryProgressText}...</p>
          )}
        </div>
      </div>
    );
  }

  const changeVersion = useCallback(
    (direction: string) => {
      setCurrentIndex((prevIndex) => {
        if (direction === "next" && prevIndex < totalVersions - 1) {
          return prevIndex + 1;
        } else if (direction === "prev" && prevIndex > 0) {
          return prevIndex - 1;
        }
        return prevIndex;
      });
    },
    [totalVersions]
  );

  const handleDownloadDocument = async (summary: string, datetime: string) => {
    setIsDownloadingDocument(true);
    const cleanedSummary = summary
      .replace(/<h1>.*?<\/h1>/g, "")
      .replace(/^\s*[\r\n]/gm, "");

    try {
      const requestBody = {
        text: cleanedSummary,
        userID: uid,
      };

      const response = await fetch(
        "https://europe-west2-motics-consultations.cloudfunctions.net/text_to_docx",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      // Assuming the response is a downloadable file
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `motics_summary_${datetime.replace(/:/g, "-")}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Failed to download the document. Please try again.");
    } finally {
      setIsDownloadingDocument(false);
    }
  };

  function renderSummaryNavigationButtons() {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "2rem",
        }}
      >
        <button
          className={`button-text ${
            currentIndex === 0 ? "button-text-disabled" : ""
          }`}
          onClick={() => changeVersion("prev")}
          disabled={currentIndex === 0}
        >
          &lt;
        </button>
        <span
          style={{ color: "grey", marginLeft: "0.3rem", marginRight: "0.3rem" }}
        >
          {currentIndex + 1} / {totalVersions}
        </span>
        <button
          className={`button-text ${
            currentIndex === totalVersions - 1 ? "button-text-disabled" : ""
          }`}
          onClick={() => changeVersion("next")}
          disabled={currentIndex === totalVersions - 1}
        >
          &gt;
        </button>
      </div>
    );
  }

  function formatDate(isoString: string): { date: string; time: string } {
    const dateObj = new Date(isoString);

    // Extract date components
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const year = dateObj.getFullYear();

    // Extract time components and convert to 12-hour format
    let hours = dateObj.getHours();
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    // Convert hour from 24-hour to 12-hour format
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'

    // Add leading zero to single-digit hour
    const formattedHours = String(hours).padStart(2, "0");

    return {
      date: `${day}-${month}-${year}`,
      time: `${formattedHours}:${minutes} ${ampm}`,
    };
  }

  const formatTimestamp = (
    timestamp: Date | { seconds: number; nanoseconds: number }
  ) => {
    const date =
      timestamp instanceof Date
        ? timestamp
        : new Date(timestamp.seconds * 1000);
    return date
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", "");
  };

  const formatDob = (dob: string) => {
    try {
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(dob));
    } catch (error) {
      return "Not provided";
    }
  };

  const [isPushingToMeddbase, setIsPushingToMeddbase] = useState(false);

  // Helper function to generate a document name
  const generateDocumentName = () => {
    if (meddbasePID) {
      const date = new Date().toISOString().split("T")[0];
      return `Summary_${meddbasePID.patientName.givenName}_${meddbasePID.patientName.familyName}_${date}.pdf`;
    }
    return;
  };

  const handlePushToMeddbase = async () => {
    setIsPushingToMeddbase(true);

    try {
      let pushResult;
      let updateData: any = {};
      let formattedData: MeddbasePID;

      if (isMeddbasePatientInfoCorrect && meddbasePID) {
        // Use existing meddbasePID information
        pushResult = await pushToMeddbase(
          processSummary(null, showPushToMeddbaseModal.summary),
          meddbasePID,
          showPushToMeddbaseModal.noteType
        );
        // Prepare update data
        if (meddbasePID.patientID?.idNumber) {
          updateData.patientId = meddbasePID.patientID.idNumber;
        }
        if (
          meddbasePID.patientName?.givenName &&
          meddbasePID.patientName?.familyName
        ) {
          updateData.patientName = `${meddbasePID.patientName.givenName} ${meddbasePID.patientName.familyName}`;
        }
        if (meddbasePID.dateTimeOfBirth) {
          updateData.patientDOB = meddbasePID.dateTimeOfBirth;
        }
        if (meddbasePID.administrativeSex) {
          updateData.patientSex = meddbasePID.administrativeSex;
        }
      }

      if (pushResult && pushResult.success && pushResult.hl7Message) {
        updateData.isPushedToMeddbase = true;
        updateData.pushedSummary = showPushToMeddbaseModal.summary;
        updateData.meddbaseResponse = pushResult.meddbaseResponse;
        updateData.pushedHl7Message = pushResult.hl7Message;
        updateData.pushedToMeddbaseAt = new Date();

        const recordingDocRef = doc(
          db,
          `users/${uid}/recordings/${showPushToMeddbaseModal.recordingId}`
        );

        await updateDoc(recordingDocRef, updateData);

        const patientName = updateData.patientName || "Unknown";

        alert(`Summary pushed to ${patientName}'s Meddbase record.`);

        // Close the modal only after successful push
        setShowPushToMeddbaseModal({
          isVisible: false,
          recordingId: "",
          noteType: "",
          summary: "",
        });
      } else {
        if (pushResult) {
          updateData.pushedToMeddbaseAt = new Date();
          updateData.pushedHl7Message = pushResult.hl7Message;
          updateData.meddbaseResponse = pushResult.meddbaseResponse;
          updateData.pushToMeddbaseError = pushResult.error;

          const recordingDocRef = doc(
            db,
            `users/${uid}/recordings/${showPushToMeddbaseModal.recordingId}`
          );

          await updateDoc(recordingDocRef, updateData);
        }
        throw new Error("Failed to push to Meddbase");
      }
    } catch (error) {
      console.error("Error pushing to Meddbase:", error);
      if (
        error instanceof Error &&
        error.message !== "Failed to push to Meddbase"
      ) {
        alert("Failed to push summary to Meddbase. Please try again.");
      }
    } finally {
      if (isRemoveFromQueueAfterPush) {
        try {
          if (currentUser?.meddbaseUserId && meddbasePID?.id) {
            const messageRef = doc(
              db,
              "meddbase",
              "psm",
              "users",
              currentUser.meddbaseUserId,
              "messages",
              meddbasePID.id
            );

            await updateDoc(messageRef, { actioned: true });

            // Remove the message from unactionedHl7Messages
            try {
              setUnactionedHl7Messages((prevMessages: any) =>
                prevMessages.filter(
                  (message: any) => message.id !== meddbasePID.id
                )
              );
            } catch (error) {
              console.error(
                "Error updating actioned hl7 message status:",
                error
              );
            } finally {
              const latestMessage =
                unactionedHl7Messages
                  .filter((message) => message.id !== meddbasePID.id)
                  .slice(-1)[0] || null;

              if (latestMessage) {
                setMeddbasePID({
                  ...latestMessage.pid,
                  id: latestMessage.id,
                  timestamp: latestMessage.timestamp,
                });
                setSelectedMessageId(latestMessage.id);
              } else {
                setMeddbasePID(null);
              }
            }
          }
        } catch (error) {
          console.error("Error updating message status:", error);
        }
      }
      setIsPushingToMeddbase(false);
    }
  };

  const renderPushToMeddbaseModal = () => {
    if (!showPushToMeddbaseModal.isVisible) return null;

    const formatDateOfBirth = (dateString: string): string => {
      if (!dateString || dateString.length !== 8) return "Invalid Date";

      const year = dateString.slice(0, 4);
      const month = dateString.slice(4, 6);
      const day = dateString.slice(6, 8);

      const date = new Date(`${year}-${month}-${day}`);
      return format(date, "dd/MM/yyyy");
    };

    return (
      <div className="box-sizing-default modal-background">
        <div
          className="box-sizing-default modals"
          id="regenerate-summary-modal"
          style={{ padding: "0" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
              padding: "1rem 2rem 0 2rem",
            }}
          >
            <div style={{ fontSize: "2rem", fontWeight: "500" }}>
              Push to Meddbase
            </div>
            <button
              className="small-icon-button"
              style={{ alignSelf: "center" }}
              onClick={() =>
                setShowPushToMeddbaseModal({
                  isVisible: false,
                  recordingId: "",
                  noteType: "",
                  summary: "",
                })
              }
            >
              <img src={xMarkIcon} alt="close" />
            </button>
          </div>

          <div style={{ overflow: "auto", padding: "1rem 2rem" }}>
            {meddbasePID && (
              <div>
                <p>
                  Are you sure you want to push this summary to the following
                  patient record?
                </p>
                <div style={{ marginBottom: "2rem" }}>
                  <div
                    style={{
                      backgroundColor: "var(--color-brand-dark-green)",
                      color: "white",
                      padding: "1rem",
                      borderRadius: "0.5rem",
                    }}
                  >
                    <p
                      style={{
                        color: "white",
                        fontWeight: "bold",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Client Details
                    </p>
                    <p
                      style={{
                        color: "white",
                        fontSize: "0.8rem",
                        margin: "0.25rem 0",
                      }}
                    >
                      Arrived: {formatTimestamp(meddbasePID.timestamp)}
                    </p>
                    <p
                      style={{
                        color: "white",
                        fontSize: "0.8rem",
                        margin: "0.25rem 0",
                      }}
                    >
                      ID: {meddbasePID.patientID.idNumber}
                    </p>
                    <p
                      style={{
                        color: "white",
                        fontSize: "0.8rem",
                        margin: "0.25rem 0",
                      }}
                    >
                      Name: {meddbasePID.patientName.givenName}{" "}
                      {meddbasePID.patientName.familyName}
                    </p>
                    <p
                      style={{
                        color: "white",
                        fontSize: "0.8rem",
                        margin: "0.25rem 0",
                      }}
                    >
                      Date of Birth:{" "}
                      {formatDateOfBirth(meddbasePID.dateTimeOfBirth.time)}
                    </p>
                    {meddbasePID.patientAddress.streetAddress && (
                      <p
                        style={{
                          color: "white",
                          fontSize: "0.8rem",
                          margin: "0.25rem 0",
                        }}
                      >
                        Address:{" "}
                        <>
                          {meddbasePID.patientAddress.streetAddress}{" "}
                          {meddbasePID.patientAddress.city && ", "}
                        </>
                        {meddbasePID.patientAddress.city && (
                          <>
                            {meddbasePID.patientAddress.city}{" "}
                            {meddbasePID.patientAddress.zipOrPostalCode && ", "}
                          </>
                        )}
                        {meddbasePID.patientAddress.zipOrPostalCode}
                      </p>
                    )}
                    <div
                      className="box-sizing-default alert-text"
                      style={{
                        marginTop: "0.5rem",
                        color: "white",
                        fontStyle: "italic",
                      }}
                    >
                      If this is the wrong client, please arrive the correct
                      client on Meddbase. If the client details are incorrect,
                      please update their details on Meddbase.
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <label>
                      <input
                        type="checkbox"
                        value="isMeddbasePatientInfoCorrect"
                        onChange={(e) =>
                          setIsMeddbasePatientInfoCorrect(e.target.checked)
                        }
                      />
                      Confirm client information is correct and push to
                      Meddbase.
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        value="isRemoveFromQueueAfterPush"
                        onChange={(e) =>
                          setIsRemoveFromQueueAfterPush(e.target.checked)
                        }
                      />
                      Remove patient from queue after push to Meddbase.
                    </label>
                  </div>
                </div>
              </div>
            )}
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "2rem",
                  gap: "1rem",
                }}
              >
                <button
                  className="button-link"
                  onClick={() =>
                    setShowPushToMeddbaseModal({
                      isVisible: false,
                      recordingId: "",
                      noteType: "",
                      summary: "",
                    })
                  }
                >
                  Cancel
                </button>
                <button
                  className="button-primary"
                  style={{ width: "fit-content", fontWeight: "500" }}
                  disabled={isPushingToMeddbase}
                  onClick={handlePushToMeddbase}
                >
                  Confirm Push
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  function renderSummary() {
    const assignedPatientName =
      currentRecording[0] && currentRecording[0].patientId
        ? getPatientName(currentRecording[0].patientId)
        : null;
    //  const tempSummary = `### SOAP Notes\n\n**Subjective:**\n- The patient reports achy pain in the back, which is particularly severe a couple of days a week.\n- Pain is eased by hot water.\n- No reported issues with the neck, private area, or systemic issues like gut problems.\n- No history of hernias or prolapses.\n- The patient has a history of broken bones but no other medical conditions or allergies.\n- The patient engages in Pilates (mat work) and swimming once a week.\n- Enjoys cooking and gardening but finds them difficult due to pain.\n- No reported dislocations without trauma.\n- No family history of Ehlers-Danlos Syndrome (EDS).\n- The patient wears tube grips for joint support and finds tight clothing provides some compression and relief.\n- The patient has a dog and discusses the impact of pet care on their routine.\n- Concerns about the impact of a potential EDS diagnosis on insurance coverage.\n\n**Objective:**\n- No red flags identified in the follow-up.\n- No neurological examination, spinal palpation, or joint mobility assessment was reported.\n- No special tests, gait analysis, provocation tests, or functional assessments were reported.\n- The patient demonstrates difficulty with balance and strength on one leg.\n- The patient was instructed on exercises for scapular control, core strengthening, wrist extension, balance, and neutral spine positioning.\n\n**Assessment:**\n- Musculoskeletal pain in the back with intermittent severe episodes.\n- Possible hypermobility issues but does not meet the criteria for EDS.\n- Weakness in hamstrings and need for core strengthening.\n\n**Plan:**\n- Treatment included education on the condition, manual therapy guidance, and a home exercise program.\n- Home exercise program to include:\n  - Bridging exercises\n  - Arm lifts\n  - Wrist extension with resistance\n  - Balance practice\n  - Neutral spine positioning practice\n  - Scapular control exercises\n- The patient was advised to do strengthening exercises twice a week and to avoid doing them on Pilates days.\n- The patient was advised to practice the exercises for about 10 minutes a day, except on Pilates days.\n\n**Next Appointment Details:**\n- The patient is to return for a follow-up to assess progress and adjust the home exercise program as needed.\n\n### Tasks\n\n**Patient:**\n- Practice the prescribed home exercise program daily, except on Pilates days.\n- Notice if wearing tight clothing provides joint support and relief.\n- Consider the impact of pet care activities on symptoms and adjust as needed.\n- Follow up on the recommendation to explore Jean DeBond's app for additional exercises.\n- Attend the next scheduled appointment and report any changes or concerns.\n\n**Physio:**\n- Send the patient an email with details of the home exercise program and any relevant resources.\n- Prepare for the next appointment by reviewing the patient's progress and planning potential adjustments to the treatment plan.\n\n**GP/Family Member/Other Specialty:**\n- No tasks assigned.\n\n**Letter to GP:**\n\nDear [General Practitioner],\n\nI am writing to update you on the physiotherapy management of our mutual patient, [Patient Name], who presented with intermittent severe back pain. The patient reports that the pain is achy in nature and is significantly eased by hot water. There is no history of systemic issues, hernias, or prolapses, and the patient has not experienced any dislocations without trauma.\n\nDuring the physiotherapy assessment, we did not identify any red flags. The patient does not meet the criteria for Ehlers-Danlos Syndrome (EDS), although they exhibit some features suggestive of hypermobility. The patient has been engaging in Pilates and swimming, which they find beneficial, but they experience difficulty with certain activities due to pain.\n\nThe treatment plan includes a home exercise program focusing on scapular control, core strengthening, wrist extension, balance, and neutral spine positioning. The patient has been instructed to practice these exercises daily, with a duration of approximately 10 minutes, except on days when they attend Pilates classes.\n\nI have advised the patient on the potential impact of a diagnosis of EDS on insurance coverage and recommended caution in pursuing this diagnosis without clear evidence. The patient has been provided with support strategies, including the use of tube grips and tight clothing for joint support.\n\nPlease find enclosed the details of the home exercise program. I will continue to monitor the patient's progress and adjust the treatment plan as necessary. Should you have any further insights into the patient's condition or additional recommendations, please do not hesitate to contact me.\n\nThank you for your continued collaboration in the care of our patient.\n\nYours sincerely,\n\n[Physiotherapist Name]\n[Contact Information]`;

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setTabValue(newValue);
    };

    const formatted = timestamp
      ? formatDate(timestamp.toDate().toISOString())
      : { date: "", time: "" };

    return (
      <>
        <div className="box-sizing-default transcribe-center-container">
          <div style={{ marginTop: "2rem" }}>
            <Box>
              <StyledTabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="summary tabs"
              >
                <StyledTab label="Summary" />
                <StyledTab label="Transcript" />
                <StyledTab label="Notes" />
                {/* <StyledTab label="CLIPPE" /> */}
              </StyledTabs>
            </Box>
          </div>
          <div className="box-sizing-default transcribe-summary-container">
            {tabValue === 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignSelf: "flex-end",
                  marginBottom: "1rem",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    alignSelf: "flex-end",
                  }}
                >
                  <button
                    className="button-copy"
                    onClick={handleCopyToClipboard}
                  >
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <div>
                  {clinikoApiKey && (
                    <button
                      className="button-copy"
                      style={{ alignSelf: "left" }}
                      onClick={() => openAssignToPatientModal()}
                    >
                      Push to Cliniko
                    </button>
                  )}
                  {meddbasePID && (
                    <Tooltip
                      title="This will push the current summary or letter in view to Meddbase as a PDF."
                      arrow
                    >
                      <button
                        className="button-copy"
                        style={{ alignSelf: "left" }}
                        onClick={() =>
                          setShowPushToMeddbaseModal({
                            isVisible: true,
                            recordingId: currentRecording[0].id,
                            noteType: currentRecording[0].noteType,
                            summary:
                              summaryVersions &&
                              summaryVersions.length > 0 &&
                              currentIndex >= 0 &&
                              currentIndex < summaryVersions.length
                                ? summaryVersions[currentIndex].summary
                                : summary,
                          })
                        }
                        disabled={isPushingToMeddbase}
                      >
                        Push to Meddbase
                      </button>
                    </Tooltip>
                  )}
                  <Tooltip
                    title="Download the current summary or letter in view as a DOCX file."
                    arrow
                  >
                    <button
                      className="button-copy"
                      disabled={isDownloadingDocument}
                      onClick={() =>
                        handleDownloadDocument(
                          summaryVersions &&
                            summaryVersions.length > 0 &&
                            currentIndex >= 0 &&
                            currentIndex < summaryVersions.length
                            ? summaryVersions[currentIndex].summary
                            : summary,
                          `${formatted.date}_${formatted.time}`
                        )
                      }
                    >
                      Download as DOCX
                    </button>
                  </Tooltip>
                </div>
              </div>
            )}
            {currentRecording[0] && currentRecording[0].patientId && (
              <h4>
                Summary Assigned to Patient: {currentRecording[0].patientName}
              </h4>
            )}
            {tabValue === 0 && (
              <div style={{ width: "100%" }}>
                {summaryProgressText === "Error" && (
                  <p>
                    Error generating summary due to server outtage. Please retry
                    generating summary later at the History tab. For now, please
                    find your raw transcript in the 'Transcript' tab.
                  </p>
                )}
                {summaryVersions.length > 0 && (
                  <div
                    className={
                      loadingRegenerateSummary ? "grey-text-container" : ""
                    }
                  >
                    {/* <p
                    dangerouslySetInnerHTML={{
                      __html: summaryVersions[currentIndex].summary,
                    }}
                  ></p> */}
                    {summaryVersions.length > 0 &&
                      currentIndex >= 0 &&
                      currentIndex < summaryVersions.length && (
                        // console.log("summaryVersions: ", summaryVersions),
                        //  console.log("currentIndex: ", currentIndex),
                        //  console.log("summaryVersions[currentIndex].summary: ", summaryVersions[currentIndex].summary),

                        <TipTap
                          key={currentIndex}
                          content={summaryVersions[currentIndex].summary}
                          alwaysFocus={false}
                          // content={processSummary("Test",tempSummary)}
                          onSaveContent={(newContent) => {
                            updateContentOnFirebase(
                              currentRecording[0].id,
                              summaryVersions[currentIndex].id,
                              newContent,
                              "summary"
                            );
                          }}
                        />
                      )}
                    {/* : (
                    // Handle the case when currentIndex is out of bounds or summaryVersions is empty
                    <TipTap
                      key={currentIndex}
                      content={
                        summaryVersions[summaryVersions.length - 1].summary
                      }
                      onSaveContent={(newContent) => {
                        updateContentOnFirebase(
                          currentRecording[0].id,
                          summaryVersions[currentIndex].id,
                          newContent,
                          "summary"
                        );
                      }}
                    />
                  )} */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginTop: "2rem",
                      }}
                    >
                      {renderSummaryNavigationButtons()}
                    </div>
                    <div
                      style={{
                        marginTop: "1rem",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        className="button-copy"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: loadingRegenerateSummary
                            ? "not-allowed"
                            : "pointer",
                        }}
                        onClick={() =>
                          handleOpenRegenerateDialog(
                            currentRecording[0].id,
                            summary,
                            timestamp,
                            currentIndex
                          )
                        }
                        disabled={loadingRegenerateSummary}
                      >
                        {loadingRegenerateSummary ? (
                          <img
                            src={loadingSpinner}
                            alt="Loading"
                            style={{
                              width: "1.5rem",
                              marginRight: "0.8rem",
                            }}
                          />
                        ) : (
                          <AutoFixHighIcon sx={{ mr: 1 }} />
                        )}
                        Regenerate
                      </button>
                    </div>
                  </div>
                )}
                {summaryProgressText !== "Error" &&
                  summaryVersions.length === 0 &&
                  summary && (
                    <div
                      className={
                        loadingRegenerateSummary ? "grey-text-container" : ""
                      }
                    >
                      {/* <p dangerouslySetInnerHTML={{ __html: summary }}></p> */}
                      <TipTap
                        content={summary}
                        alwaysFocus={false}
                        onSaveContent={(newContent) => {
                          updateContentOnFirebase(
                            currentRecording[0].id,
                            "",
                            newContent,
                            "summary"
                          );
                        }}
                      />
                      <div
                        style={{
                          marginTop: "3rem",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          className="button-copy"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            cursor: loadingRegenerateSummary
                              ? "not-allowed"
                              : "pointer",
                          }}
                          onClick={() =>
                            handleOpenRegenerateDialog(
                              currentRecording[0].id,
                              summary,
                              timestamp,
                              currentIndex
                            )
                          }
                          disabled={loadingRegenerateSummary}
                        >
                          {loadingRegenerateSummary ? (
                            <img
                              src={loadingSpinner}
                              alt="Loading"
                              style={{
                                width: "1.5rem",
                                marginRight: "0.8rem",
                              }}
                            />
                          ) : (
                            <AutoFixHighIcon sx={{ mr: 1 }} />
                          )}
                          Regenerate
                        </button>
                      </div>
                    </div>
                  )}
              </div>
            )}
            {tabValue === 1 && (
              <div style={{ width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    className="button-copy"
                    onClick={handleCopyToClipboard}
                  >
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                {/* <p dangerouslySetInnerHTML={{ __html: transcription }}></p> */}
                <TipTap
                  content={transcription.replace(/\n/g, "<br />")}
                  alwaysFocus={false}
                  onSaveContent={(newContent) => {
                    updateContentOnFirebase(
                      currentRecording[0].id,
                      "",
                      newContent,
                      "transcription"
                    );
                  }}
                />
              </div>
            )}
            {tabValue === 2 && (
              <div style={{ width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    className="button-copy"
                    onClick={handleCopyToClipboard}
                  >
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                {/* <p dangerouslySetInnerHTML={{ __html: sessionNotes }}></p> */}
                <TipTap
                  content={sessionNotes.replace(/\n/g, "<br />")}
                  alwaysFocus={false}
                  onSaveContent={(newContent) => {
                    updateContentOnFirebase(
                      currentRecording[0].id,
                      "",
                      newContent,
                      "notes"
                    );
                  }}
                />
              </div>
            )}
            {tabValue === 3 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {clippeOutput && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        alignSelf: "flex-end",
                        marginBottom: "1rem",
                      }}
                    >
                      <button
                        className="button-copy"
                        onClick={handleCopyToClipboard}
                      >
                        {copied ? "Copied" : "Copy"}
                      </button>
                    </div>
                    {clippeOutput.split("\n").map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </div>
                )}
                {!clippeOutput && (
                  <button
                    className="button-copy"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      alignSelf: "center",
                      cursor: loadingAnalyseWithClippe
                        ? "not-allowed"
                        : "pointer",
                    }}
                    onClick={() =>
                      handleAnalyseWithClippe(currentRecording[0].id, summary)
                    }
                    disabled={loadingAnalyseWithClippe}
                  >
                    {loadingAnalyseWithClippe ? (
                      <img
                        src={loadingSpinner}
                        alt="Loading"
                        style={{
                          width: "1.5rem",
                          marginRight: "0.8rem",
                        }}
                      />
                    ) : (
                      <AttachmentIcon sx={{ mr: 1 }} />
                    )}
                    Analyse with CLIPPE
                  </button>
                )}
                {/* <p dangerouslySetInnerHTML={{ __html: sessionNotes }}></p> */}
                {/* <TipTap
                  content={sessionNotes}
                  alwaysFocus={false}
                  onSaveContent={(newContent) => {
                    updateContentOnFirebase(
                      currentRecording[0].id,
                      "",
                      newContent,
                      "notes"
                    );
                  }}
                /> */}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  const toggleMode = (selectedMode: "generate" | "dictate") => {
    setMode(selectedMode);
  };

  const renderRegenerateSummaryModal = () =>
    // recId: string,
    // recSummary: string,
    // recTimeStamp: any,
    // index: number
    {
      if (!openRegenerateDialog) {
        return null;
      }
      const recId = toRegenerateRecId;
      const recSummary = toRegenerateRecSummary;
      const recTimeStamp = toRegenerateRecTimeStamp;
      const index = toRegenerateIndex;
      const handleTemplateChange = async (e: SelectChangeEvent) => {
        const templateId = e.target.value;
        setRegenerateSelectedTemplate(templateId);

        if (!regenerateSelectedCategory) return;

        const template =
          combinedTemplates[regenerateSelectedCategory] &&
          combinedTemplates[regenerateSelectedCategory].find(
            (t) => t.id === templateId
          );
        if (template) {
          setRegenerateSelectedDisplayName(template.displayName);
          const isPresetTemplate = await checkIfPreset(
            template.id,
            regenerateSelectedCategory
          );

          setRegenerateIsPreset(isPresetTemplate);
        }
      };

      async function checkIfPreset(
        templateId: string,
        category: keyof Templates
      ): Promise<boolean> {
        // Simulate checking logic
        return new Promise((resolve) => {
          let isPreset = false;
          if (presetTemplates[category]) {
            isPreset = presetTemplates[category].some(
              (presetTemplate) => presetTemplate.id === templateId
            );
          }
          resolve(isPreset);
        });
      }

      // if (recNoteType) {
      //   setChangeNoteType(recNoteType);
      // }
      return (
        <div className="box-sizing-default modal-background">
          <div
            className="box-sizing-default modals"
            id="regenerate-summary-modal"
            style={{ maxHeight: "90%", padding: "0" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
                padding: "1rem 2rem 0 2rem",
              }}
            >
              <div style={{ fontSize: "2rem", fontWeight: "500" }}>
                Regenerate Summary
              </div>
              <button
                className="small-icon-button"
                onClick={handleCloseRegenerateDialog}
                style={{ alignSelf: "center" }}
              >
                <img
                  src={xMarkIcon}
                  className="small-icon-button"
                  alt="close"
                />
              </button>
            </div>
            <div style={{ overflow: "auto", padding: "1rem 2rem" }}>
              {/* <p>Please select summary regeneration note type.</p> */}
              <div
                style={{
                  fontSize: "1rem",
                  fontWeight: "bold",
                  textAlign: "center",
                  marginBottom: "1rem",
                }}
              >
                Choose a template
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "2rem",
                  marginBottom: "2rem",
                  width: "100%",
                }}
              >
                <div style={{ width: "40%" }}>
                  <div>Note Category</div>
                  <FormControl fullWidth size="small">
                    <Select
                      labelId="demo-select-small-label"
                      id="demo-select-small"
                      value={regenerateSelectedCategory}
                      onChange={(e: SelectChangeEvent) => {
                        setRegenerateSelectedCategory(
                          e.target.value as keyof Templates
                        );
                        setRegenerateSelectedTemplate("");
                      }}
                    >
                      {Object.keys(combinedTemplates).map((category) => (
                        <MenuItem key={category} value={category}>
                          {camelCaseToWords(category)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div style={{ width: "40%" }}>
                  <div>Note Template</div>
                  <FormControl fullWidth size="small">
                    <Select
                      labelId="demo-select-small-label"
                      id="demo-select-small"
                      value={regenerateSelectedTemplate || ""}
                      onChange={handleTemplateChange}
                      disabled={!regenerateSelectedCategory}
                    >
                      {regenerateSelectedCategory &&
                        combinedTemplates[regenerateSelectedCategory].map(
                          (template) => (
                            <MenuItem key={template.id} value={template.id}>
                              {template.displayName}
                            </MenuItem>
                          )
                        )}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                Add to transcript via audio recording:
                <RegenerateAudioRecorder
                  mode="generate"
                  onAudioUrl={handleAudioUrl}
                  onRecordingClick={handleRecordingClick}
                  userId={uid}
                  recordingId={recId}
                  sessionNotesUpload={sessionNotesUpload}
                  noteTemplate={regenerateSelectedDisplayName}
                  templateID={regenerateSelectedTemplate}
                  templateCategory={regenerateSelectedCategory}
                  templateIsPreset={regenerateIsPreset}
                  newTranscript={newTranscript}
                  setNewTranscript={setNewTranscript}
                  setIsTranscribing={setIsTranscribing}
                  isTranscribing={isTranscribing}
                />
              </div>
              {/* {regenerateSelectedCategory && (
            <>
              {console.log(
                "regenerateSelectedCategory:",
                regenerateSelectedCategory
              )}
              {console.log(
                "regenerateSelectedTemplate:",
                regenerateSelectedTemplate
              )}
            </>
          )} */}
              {/* <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "10px",
              marginBottom: "1rem",
            }}
          >
            {[
              "Adaptive AI",
              "SOAP Note",
              "SOAP Note (Short)",
              "Letter to Patient",
              "Letter to GP",
              "Referral to Speciality",
              "Custom",
            ].map((type) => (
              <button
                className={`button-classic ${
                  (recordings[index].noteType || selectedNoteType) === type
                    ? "button-classic-green"
                    : ""
                }`}
                onClick={() => {
                  recordings[index].noteType = type;
                  setRegenerateNoteType(type);
                }}
              >
                {type}
              </button>
            ))}
          </div> */}
              <p style={{ marginTop: "1rem", marginBottom: "0" }}>
                Please enter any additional instructions.
              </p>
              <div
                className="box-sizing-default alert-text"
                style={{ marginTop: "0" }}
              >
                Please note that previous additional instructions will be
                overwritten when regenerating the summary. Please include any
                previous instructions in the new instructions if they are
                needed.
              </div>
              <TextField
                style={{ marginBottom: "1rem" }}
                label="Additional instructions (e.g., 'Please include the patient's name, John Smith, in the letter.')"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={regenerateInstruction}
                onChange={(e) => setRegenerateInstruction(e.target.value)}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  margin: "1rem",
                }}
              >
                <button
                  className="button-copy"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "auto",
                    color: "white",
                    cursor: recording ? "not-allowed" : "pointer",
                  }}
                  disabled={recording || isTranscribing}
                  onClick={() =>
                    handleRegenerateSummary(
                      recId,
                      recSummary,
                      recTimeStamp || new Date()
                    )
                  }
                >
                  <AutoFixHighIcon sx={{ mr: 1 }} />
                  Regenerate
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    };

  return (
    <>
      {pageLoading ? (
        <div>Loading...</div>
      ) : (
        <>
          <div className="box-sizing-default tab-content-container">
            <div
              style={{
                fontSize: "2rem",
                fontWeight: "500",
                marginTop: "1rem",
                marginBottom: "1rem",
              }}
            >
              Start New Session
            </div>
            {meddbasePID && (
              <div>
                {unactionedHl7Messages.length > 0 && (
                  <div>
                    {/* <div
                      style={{
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Arrived Patients
                    </div> */}
                    <div
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              "Are you sure you want to clear all clients in the queue?"
                            )
                          ) {
                            // Write actioned=true to all unactionedHl7Messages on Firebase
                            const promises = unactionedHl7Messages.map(
                              (message) => {
                                if (currentUser.meddbaseUserId) {
                                  const messageRef = doc(
                                    db,
                                    "meddbase",
                                    "psm",
                                    "users",
                                    currentUser.meddbaseUserId,
                                    "messages",
                                    message.id
                                  );
                                  return updateDoc(messageRef, {
                                    actioned: true,
                                  }).catch((error) => {
                                    console.error(
                                      `Error updating message ${message.id}:`,
                                      error
                                    );
                                  });
                                }
                              }
                            );

                            Promise.all(promises)
                              .then(() => {
                                setUnactionedHl7Messages([]);
                                setMeddbasePID(null);
                              })
                              .catch((error) => {
                                console.error(
                                  "Error updating messages:",
                                  error
                                );
                              });
                          }
                        }}
                        style={{
                          padding: "0.2rem 0.5rem",
                          marginBottom: "0.2rem",
                          border: "1px solid black",
                          background: "white",
                          cursor: "pointer",
                        }}
                        disabled={recording}
                      >
                        Clear All Clients
                      </button>
                    </div>
                    <select
                      style={{
                        width: "100%",
                        marginBottom: "1rem",
                        padding: "0.2rem 0.5rem",
                      }}
                      value={selectedMessageId || meddbasePID.id}
                      onChange={async (e) => {
                        const selectedId = e.target.value;
                        setSelectedMessageId(selectedId);
                        
                        // Find the selected message
                        const selectedMessage = unactionedHl7Messages.find(
                          (msg) => msg.id === selectedId
                        );

                        if (selectedMessage) {
                          setMeddbasePID({
                            ...selectedMessage.pid,
                            id: selectedId,
                            timestamp: selectedMessage.timestamp,
                          } as MeddbasePID);

                          // Update the user document with the selected message data
                          const userDocRef = doc(db, "users", uid);
                          try {
                            await updateDoc(userDocRef, {
                              currentMeddbasePID: {
                                ...selectedMessage.pid,
                                id: selectedId,
                                timestamp: selectedMessage.timestamp,
                              },
                              currentMeddbasePV1: {
                                ...selectedMessage.pv1,
                                id: selectedId,
                                timestamp: selectedMessage.timestamp,
                              },
                            });
                          } catch (error) {
                            console.error("Error updating user record:", error);
                            // Consider adding user-facing error handling here if necessary
                          }
                        }
                      }}
                      disabled={recording}
                    >
                      <option value="">Select a patient</option>
                      {unactionedHl7Messages.map((message) => (
                        <option key={message.id} value={message.id}>
                          {`${formatTimestamp(message.timestamp)} - ${
                            message.pid.patientName.givenName
                          } ${message.pid.patientName.familyName} (${
                            message.pid.patientID.idNumber
                          })`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to clear the current active client?"
                        )
                      ) {
                        if (
                          currentUser.meddbaseUserId &&
                          (selectedMessageId || meddbasePID.id)
                        ) {
                          const messageRef = doc(
                            db,
                            "meddbase",
                            "psm",
                            "users",
                            currentUser.meddbaseUserId,
                            "messages",
                            selectedMessageId || meddbasePID.id
                          );

                          updateDoc(messageRef, { actioned: true })
                            .then(() => {
                              // Remove the message from unactionedHl7Messages
                              setUnactionedHl7Messages((prevMessages: any) =>
                                prevMessages.filter(
                                  (message: any) =>
                                    message.id !== selectedMessageId
                                )
                              );

                              // Set selectedMessageId and meddbasePID to null
                              setSelectedMessageId("");
                              setMeddbasePID(null);
                            })
                            .catch((error) => {
                              console.error(
                                `Error updating message ${selectedMessageId}:`,
                                error
                              );
                            });
                        }
                      }
                    }}
                    style={{
                      padding: "0.2rem 0.5rem",
                      marginBottom: "0.2rem",
                      border: "1px solid black",
                      background: "white",
                      cursor: "pointer",
                    }}
                    disabled={recording}
                  >
                    Clear Client
                  </button>
                </div>
                <div
                  style={{
                    marginBottom: "2rem",
                    backgroundColor: "var(--color-brand-dark-green)",
                    color: "white",
                    padding: "1rem",
                    borderRadius: "0.5rem",
                  }}
                >
                  <p
                    style={{
                      color: "white",
                      fontWeight: "bold",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Client Details
                  </p>
                  <p
                    style={{
                      color: "white",
                      fontSize: "0.8rem",
                      margin: "0.25rem 0",
                    }}
                  >
                    Arrived: {formatTimestamp(meddbasePID.timestamp)}
                  </p>
                  <p
                    style={{
                      color: "white",
                      fontSize: "0.8rem",
                      margin: "0.25rem 0",
                    }}
                  >
                    ID: {meddbasePID.patientID.idNumber}
                  </p>
                  <p
                    style={{
                      color: "white",
                      fontSize: "0.8rem",
                      margin: "0.25rem 0",
                    }}
                  >
                    Name: {meddbasePID.patientName.givenName}{" "}
                    {meddbasePID.patientName.familyName}
                  </p>
                  <p
                    style={{
                      color: "white",
                      fontSize: "0.8rem",
                      margin: "0.25rem 0",
                    }}
                  >
                    Date of Birth:{" "}
                    {formatDateOfBirth(meddbasePID.dateTimeOfBirth.time)}
                  </p>
                  {meddbasePID.patientAddress.streetAddress && (
                    <p
                      style={{
                        color: "white",
                        fontSize: "0.8rem",
                        margin: "0.25rem 0",
                      }}
                    >
                      Address:{" "}
                      <>
                        {meddbasePID.patientAddress.streetAddress}{" "}
                        {meddbasePID.patientAddress.city && ", "}
                      </>
                      {meddbasePID.patientAddress.city && (
                        <>
                          {meddbasePID.patientAddress.city}{" "}
                          {meddbasePID.patientAddress.zipOrPostalCode && ", "}
                        </>
                      )}
                      {meddbasePID.patientAddress.zipOrPostalCode}
                    </p>
                  )}
                  <div
                    className="box-sizing-default alert-text"
                    style={{
                      marginTop: "0.5rem",
                      color: "white",
                      fontStyle: "italic",
                    }}
                  >
                    If this is the wrong client, please arrive the correct
                    client on Meddbase. If the client details are incorrect,
                    please update their details on Meddbase.
                  </div>
                </div>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "0.8rem",
              }}
            >
              <button
                className={`button-session-mode ${
                  mode === "generate" ? "active-mode" : ""
                }`}
                onClick={() => {
                  toggleMode("generate");
                  setAudioUrl("");
                  setSummaryAvailable(false);
                }}
                disabled={recording || !!(audioUrl && !summaryAvailable)}
              >
                Generate Summary
              </button>
              <button
                className={`button-session-mode ${
                  mode === "dictate" ? "active-mode" : ""
                }`}
                onClick={() => {
                  toggleMode("dictate");
                  setAudioUrl("");
                  setSummaryAvailable(false);
                }}
                disabled={recording || !!(audioUrl && !summaryAvailable)}
              >
                Dictate
              </button>
            </div>
            {mode === "generate" && (
              <>
                {renderNoteTypeSelector()}
                {!uploadingAudioFile && (
                  <AudioRecorder
                    mode={mode}
                    onAudioUrl={handleAudioUrl}
                    onRecordingClick={handleRecordingClick}
                    sessionNotesUpload={sessionNotesUpload}
                    noteTemplate={selectedTemplate}
                    templateID={templateID}
                    templateCategory={templateCategory}
                    templateIsPreset={isPreset}
                  />
                )}
                {/* Add a divider between the audio recorder and an option to upload audio file. */}
                <div style={{ textAlign: "center", marginBottom: "2rem" }}>
                  --- OR ---
                </div>
                <div style={{ textAlign: "center", marginBottom: "2rem" }}>
                  <input
                    id="audio-upload"
                    type="file"
                    accept="audio/*, video/*"
                    onChange={handleFileChange}
                    disabled={uploadingAudioFile}
                    style={{
                      border: "1px solid #ccc",
                      padding: "0.5rem",
                      borderRadius: "4px",
                      width: "300px",
                    }}
                  />
                  <button
                    className="button-record"
                    onClick={() => {
                      if (selectedTemplate === "") {
                        alert(
                          "Please select a note template before starting recording."
                        );
                      } else {
                        handleFileUpload();
                      }
                    }}
                    disabled={
                      uploadingAudioFile || !uploadFile || !!errorUploadMessage
                    }
                  >
                    {uploadingAudioFile
                      ? "Uploading Audio File..."
                      : "Upload Audio File"}
                  </button>
                </div>
                {!recording &&
                  audioUrl &&
                  !summaryAvailable &&
                  renderGenerateSummary()}
                {!recording && audioUrl && summaryAvailable && renderSummary()}
                {/* {renderSummary()} */}

                {recording && (
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "500",
                      textAlign: "center",
                    }}
                  >
                    Please add any session notes here. At "Stop Recording", your
                    notes will be used along with the transcript to help
                    generate a summary.
                    <NoteContainer
                      onSaveContent={(newContent) => {
                        setSessionNotesUpload(newContent);
                      }}
                    />
                  </div>
                )}
              </>
            )}
            {mode === "dictate" && (
              <div style={{ marginTop: "2rem" }}>
                {!uploadingAudioFile && (
                  <AudioRecorder
                    mode={mode}
                    onAudioUrl={handleAudioUrl}
                    onRecordingClick={handleRecordingClick}
                    sessionNotesUpload={sessionNotesUpload}
                    noteTemplate={selectedTemplate}
                    templateID={templateID}
                    templateCategory={templateCategory}
                    templateIsPreset={isPreset}
                  />
                )}
                <div style={{ textAlign: "center", marginBottom: "2rem" }}>
                  --- OR ---
                </div>
                <div style={{ textAlign: "center" }}>
                  <input
                    id="audio-upload"
                    type="file"
                    accept="audio/*,video/*,.mp3,.mp4,.m4a,.aac,.wav,.ogg,.webm"
                    onChange={handleFileChange}
                    disabled={uploadingAudioFile}
                    style={{
                      border: "1px solid #ccc",
                      padding: "0.5rem",
                      borderRadius: "4px",
                      width: "300px",
                    }}
                  />
                  <button
                    className="button-record"
                    onClick={handleFileUpload}
                    disabled={
                      uploadingAudioFile || !uploadFile || !!errorUploadMessage
                    }
                  >
                    {uploadingAudioFile
                      ? "Uploading Audio File..."
                      : "Upload Audio File"}
                  </button>
                </div>
                {!recording &&
                  audioUrl &&
                  !summaryAvailable &&
                  renderGenerateSummary()}
                {!recording && audioUrl && summaryAvailable && renderSummary()}
              </div>
            )}
          </div>
        </>
      )}

      {openRegenerateDialog && renderRegenerateSummaryModal()}
      {showAssignToPatientModal && renderAssignToPatientModal()}
      {renderPushToMeddbaseModal()}
    </>
  );
};
export default TranscribeTabContent;
